import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { format, addDays } from 'date-fns';
import medicalhistory from '../../assets/img/medicalHistory.svg';
import emptymedicalhistory from '../../assets/img/emptyymedicalhistory.svg';
import { useDispatch, useSelector } from 'react-redux';
import Swal from "sweetalert2";
import { toast } from 'react-toastify';
import { ArrowLeftCircle, ArrowRightCircle, MoreVertical, ArrowLeft, ArrowRight, ChevronRight, CalendarCheck, Video, FileText, MessageSquare, Send, Download } from 'lucide-react';
import {
    cancelAppointment,
    getAppointmentById,
    updateDoctorNotes,
    updatePreparationNotes,
    updateWrittenNotes,
    updateSummaryNotes,
    uploadAudio,
    uploadImage,
    deleteUploadedImage,
    deleteAudio,
    updateDoctorNotes1,
    updateAppointment,
    getRescheduleRecommendation,
    rescheduleAppointment,
    getDoctorAvailableSlots,
    getAvailableSlots,
    applyMediaResult
} from '../../redux/appointmentSlice';
import { getSocket } from '../../socket';
import { extractErrorMessage } from '../../utils/extractErrorMessage';
import { getPatientById, getMedicalHistory } from '../../redux/patientSlice';
import { getDoctorSettings } from '../../redux/doctorSlice';
import { getFileURL } from '../../utils/fileURL';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import baseURL from '../../utils/baseURL';
import { useNavigate } from 'react-router-dom';
import MasterLabel from '../common/MasterLabel';
import CommonLoader from '../CommonLoader';
import { X, Wallet, CheckCircle2 } from 'lucide-react';
import SummaryDrawer from './SummaryDrawer';
import AskAIDrawer from './AskAIDrawer';
import PrescriptionSection from './PrescriptionSection';
import MeetingSummary from '../MeetingSummary';
import AppointmentSummary from '../AppointmentSummary';
import { getPrescriptionFields, updateAppointmentCustomNote } from '../../redux/prescriptionFieldSlice';
import CompletedPaymentCard from '../payment/CompletedPaymentCard';
import { getPaymentOptions, payAppointment } from '../../redux/paymentSlice';

// Every value that has ever meant "paid" across the codebase (current + legacy).
const PAID_VALUES = ['Paid', 'paid', 'collected', 'Completed'];
const isPaidStatus = (s) => PAID_VALUES.includes(s);

// Offline fallback methods for the complete-appointment invoice when the backend
// payment options haven't loaded (e.g. branch not deployed on this environment).
// No "pay later" — an appointment must be settled before it can be completed.
const FALLBACK_COMPLETE_METHODS = [
    { key: 'cash', label: 'Cash' },
    { key: 'card', label: 'Card / Terminal' },
    { key: 'online', label: 'Online' },
];

// Methods a patient can settle online themselves (cash is collected in person).
const PATIENT_PAY_METHODS = [
    { key: 'card', label: 'Card / Terminal' },
    { key: 'online', label: 'Online' },
];

// Maps a seeded note field's legacyKey back to the appointment column it
// used to live in, so existing data still renders even though new saves now
// go to customNotes.
const LEGACY_KEY_TO_COLUMN = {
    communication_therapist_patient: 'description',
    notes_of_doctor: 'doctorDescription',
    preparation: 'preparationNotes',
    consultation_notes: 'writtenNotes',
    summary: 'summaryNotes',
};

// Utility function to format localTime to date and time
const formatDateTime = (localTime) => {
    if (!localTime) return { date: '', time: '' };
    const dateObj = new Date(localTime);
    const date = dateObj.toLocaleDateString('en-CA'); // Format as YYYY-MM-DD
    const time = dateObj.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    return { date, time };
};

// Maximum length constants
const MAX_NOTES_LENGTH = 2000;
const MAX_AUDIO_SIZE = 10 * 1024 * 1024; // 10MB
const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB

// Utility function to validate text input (only letters, spaces, and basic punctuation)
const isValidTextInput = (value) => {
    // Allow letters (including international characters), spaces, periods, commas, apostrophes, hyphens, and line breaks
    const textOnlyPattern = /^[a-zA-ZÀ-ÿ\s.,'\-\n\r]*$/;
    return textOnlyPattern.test(value);
};

// Validate notes textarea
const validateNotesInput = (value, t) => {
    // Sanitize input - remove potentially dangerous characters
    const sanitized = value.replace(/[<>]/g, '');

    // Check max length
    if (sanitized.length > MAX_NOTES_LENGTH) {
        return t ? t('medical_history.notes_exceed_limit', { max: MAX_NOTES_LENGTH }) : `Notes cannot exceed ${MAX_NOTES_LENGTH} characters`;
    }

    // Check for only whitespace (allow empty)
    if (sanitized.trim().length === 0 && sanitized.length > 0) {
        return t ? t('medical_history.notes_only_spaces') : 'Notes cannot contain only spaces';
    }

    return '';
};

// Helper function to get currency symbol
// Handles both object { currencySymbol, currencyCode } (from API) and string codes (legacy)
const getCurrencySymbol = (currency) => {
    if (!currency) return '';
    if (typeof currency === 'object') {
        return currency.currencySymbol || currency.currencyCode || '';
    }
    // Legacy string fallback
    if (typeof currency !== 'string') {
        return String(currency);
    }
    const upperCurrency = currency.toUpperCase();
    if (upperCurrency === 'INR') return '₹';
    if (upperCurrency === 'EUR') return '€';
    return currency;
};

const MedicalHistory = ({ selected, isOpenAppointmentMode = false }) => {
    const { t, i18n } = useTranslation(['medical_history']);
    const {
        selectedPatient,
        isLoading: patientLoading,
        error: patientError,
        medicalHistoryData,
        medicalHistoryPage,
        medicalHistoryTotalPages,
        medicalHistoryLoading
    } = useSelector((state) => state.patient);
    const { selectedAppointment, isAppointmentLoading: appointmentLoading, error: appointmentError, availableSlots, availableDoctorSlots } = useSelector((state) => state.appointment);
    const completePaymentOptions = useSelector((state) => state.payment?.paymentOptions);
    const { selectedDoctor, doctorSettings } = useSelector((state) => state.doctor);
    const { fields: prescriptionFields } = useSelector((state) => state.prescriptionField);
    const menuRef = useRef(null);
    const scrollObserverRef = useRef(null);
    const cardsContainerRef = useRef(null);
    const userRole = localStorage.getItem('Role');
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // Tracks the open appointment for socket handlers (refs avoid re-subscribing
    // the socket on every selection change).
    const selectedAppointmentIdRef = useRef(null);
    useEffect(() => {
        selectedAppointmentIdRef.current = selectedAppointment?.appointmentId || null;
    }, [selectedAppointment?.appointmentId]);

    // Live async results: the backend pushes transcript:ready / ocr:ready over
    // Socket.IO when a worker finishes. Patch the matching upload item in redux
    // so "Generating…" is replaced by the real text without a refresh. The
    // socket may not be connected yet when this mounts, so retry attaching
    // (mirrors MeetingSummary/AppointmentSummary's pattern).
    useEffect(() => {
        let alive = true;
        let retryTimer = null;
        let attachedSocket = null;
        const onTranscript = ({ appointmentId, uploadId, transcribedNotes, status }) => {
            dispatch(applyMediaResult({ appointmentId, uploadId, kind: 'audio', text: transcribedNotes, status }));
        };
        const onOcr = ({ appointmentId, uploadId, ocrNotes, status }) => {
            dispatch(applyMediaResult({ appointmentId, uploadId, kind: 'image', text: ocrNotes, status }));
        };
        // Doctor confirmed the meeting is completed → refetch the open appointment
        // so the Connect Meet button disappears instantly (no refresh).
        const onMeetingCompleted = ({ appointmentId }) => {
            if (appointmentId && appointmentId === selectedAppointmentIdRef.current) {
                dispatch(getAppointmentById(appointmentId));
            }
        };
        const attach = () => {
            if (!alive) return;
            const socket = getSocket();
            if (!socket) { retryTimer = setTimeout(attach, 1500); return; }
            attachedSocket = socket;
            socket.on('transcript:ready', onTranscript);
            socket.on('ocr:ready', onOcr);
            socket.on('meeting:completed', onMeetingCompleted);
        };
        attach();
        return () => {
            alive = false;
            if (retryTimer) clearTimeout(retryTimer);
            if (attachedSocket) {
                attachedSocket.off('transcript:ready', onTranscript);
                attachedSocket.off('ocr:ready', onOcr);
                attachedSocket.off('meeting:completed', onMeetingCompleted);
            }
        };
    }, [dispatch]);

    // Fallback for lost socket events (tab backgrounded, reconnect mid-emit,
    // etc.): while any audio/image upload on the open appointment is still
    // 'processing', poll its status directly (bypassing the getAppointmentById
    // thunk so isAppointmentLoading isn't toggled and inputs don't flicker
    // disabled) and patch redux the same way the socket handler does.
    const axiosInstance = useAxiosAuth();
    useEffect(() => {
        const appointmentId = selectedAppointment?.appointmentId;
        const hasProcessing = [
            ...(selectedAppointment?.audioUploads || []),
            ...(selectedAppointment?.imageUploads || []),
        ].some((u) => u.status === 'processing');
        if (!appointmentId || !hasProcessing) return undefined;

        let alive = true;
        const timer = setTimeout(async () => {
            if (!alive) return;
            try {
                const res = await axiosInstance.get(`${baseURL}/appointment/${appointmentId}`);
                const fresh = res.data?.data || res.data;
                (fresh?.audioUploads || []).forEach((u) => {
                    if (u.status !== 'processing') {
                        dispatch(applyMediaResult({ appointmentId, uploadId: u.uploadId, kind: 'audio', text: u.transcribedNotes, status: u.status }));
                    }
                });
                (fresh?.imageUploads || []).forEach((u) => {
                    if (u.status !== 'processing') {
                        dispatch(applyMediaResult({ appointmentId, uploadId: u.uploadId, kind: 'image', text: u.ocrNotes, status: u.status }));
                    }
                });
            } catch {
                // silent — next tick (or the socket) will catch up
            }
        }, 6000);
        return () => { alive = false; clearTimeout(timer); };
        // Re-runs whenever an upload's status/text changes (including after this
        // poll patches one), so it keeps ticking every ~6s until none are 'processing'.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, selectedAppointment?.appointmentId, selectedAppointment?.audioUploads, selectedAppointment?.imageUploads]);

    // Render an async transcript/OCR result, or a "Generating…" spinner while
    // the worker is still processing it.
    const mediaTextBlock = (text, status, generatingLabel) => {
        if (status === 'processing') {
            return (
                <p className="mt-2 w-full p-3 border border-gray-200 rounded bg-gray-50 text-sm text-gray-400 italic flex items-center gap-2">
                    <span className="h-3 w-3 border-2 border-gray-300 border-t-[#4ABDBD] rounded-full animate-spin inline-block" />
                    {generatingLabel}
                </p>
            );
        }
        if (!text) return null;
        return (
            <p className="mt-2 w-full p-3 border border-gray-300 rounded bg-gray-50 text-sm sm:text-base text-gray-600 whitespace-pre-wrap">
                {text}
            </p>
        );
    };

    // State management
    const [selectedTab, setSelectedTab] = useState('Booked');
    const [isSummaryDrawerOpen, setIsSummaryDrawerOpen] = useState(false);
    const [isAskAIDrawerOpen, setIsAskAIDrawerOpen] = useState(false);
    const [isOpenDetails, setIsOpenDetails] = useState(false);
    // Tracks which inline note textareas are expanded (taller). Keyed by `section.section`.
    const [expandedSections, setExpandedSections] = useState({});
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    // 'complete' = doctor collects payment + completes the appointment.
    // 'pay'      = patient settles online/card before the appointment is completed.
    const [paymentModalMode, setPaymentModalMode] = useState('complete');
    const [paymentMethod, setPaymentMethod] = useState('Cash');
    const [selectedCard, setSelectedCard] = useState({
        speciality: '',
        type: '',
        color: '#000000',
        date: '',
        time: '',
        appointmentId: ''
    });
    const [showDetails, setShowDetails] = useState(false);
    const [openMenuIndex, setOpenMenuIndex] = useState(null);
    const [audioFile, setAudioFile] = useState(null);
    const [audioPreview, setAudioPreview] = useState(null);
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    // Stores only the uploadId (or 'legacy'), not the upload object — so the
    // detail view below always re-derives the live item from redux instead of
    // a frozen snapshot, and picks up OCR results/status as they arrive.
    const [viewingImageUpload, setViewingImageUpload] = useState(null);
    const viewingImageUploadLive = useMemo(() => {
        if (!viewingImageUpload) return null;
        if (viewingImageUpload === 'legacy') {
            return { uploadId: 'legacy', imageUrl: selectedAppointment?.imageUrl, ocrNotes: selectedAppointment?.ocrNotes, status: 'done' };
        }
        return (selectedAppointment?.imageUploads || []).find((u) => u.uploadId === viewingImageUpload) || null;
    }, [viewingImageUpload, selectedAppointment]);
    const scrollPositionRef = useRef(0);
    const shouldRestoreScrollRef = useRef(false);
    const [isCardLoading, setIsCardLoading] = useState(false);
    const [savingField, setSavingField] = useState(null);
    const isSaving = savingField !== null;
    // Doctor-configured visibility for the four built-in feature blocks lives
    // on doctorSettings.prescriptionSections; default to all-on if absent.
    const visibleStaticTypes = useMemo(() => {
        const config = doctorSettings?.prescriptionSections || {
            medicines: true, audio: true, image: true,
        };
        const set = new Set();
        Object.entries(config).forEach(([k, v]) => { if (v) set.add(k); });
        return set;
    }, [doctorSettings]);
    const visibleNoteFields = useMemo(
        () => prescriptionFields.filter((f) => f.visible !== false),
        [prescriptionFields]
    );
    const [notesErrors, setNotesErrors] = useState({});
    const [audioError, setAudioError] = useState('');
    const [imageError, setImageError] = useState('');
    const originalNotesRef = useRef({});
    const pendingNavigationRef = useRef(null);
    const [notes, setNotes] = useState({});
    const [hasCalledHandleCardClick, setHasCalledHandleCardClick] = useState(false); // Track if handleCardClick was called
    const previousPatientIdRef = useRef(null); // Track previous patientId to detect changes
    const previousTabRef = useRef(null); // Track previous tab to detect changes
    const hasInitialFetchRef = useRef(false); // Track if initial fetch has been completed
    const isInitialMountRef = useRef(true); // Track if this is the initial mount
    const isFetchingRef = useRef(false); // Track if API call is in progress to prevent concurrent calls
    const [currentRecommendation, setCurrentRecommendation] = useState(null); // Store current reschedule recommendation data
    const [isViewingMoreSlots, setIsViewingMoreSlots] = useState(false); // Track if viewing all available slots
    const [weekOffsetMore, setWeekOffsetMore] = useState(0); // Week offset for view more slots
    const [openDayMore, setOpenDayMore] = useState(null); // Opened day for view more slots
    const [isSwitchingTab, setIsSwitchingTab] = useState(false); // Track if user is switching tabs
    const systemTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone; // Get system timezone for reschedule
    const detailsContainerRef = useRef(null); // Ref for scrolling in medical history details

    // Check if there are unsaved changes in any notes field
    const hasUnsavedChanges = () => {
        const original = originalNotesRef.current;
        if (!original || Object.keys(original).length === 0) return false;
        return Object.keys(notes).some(key => notes[key] !== (original[key] || ''));
    };

    // Confirm navigation when there are unsaved changes
    const confirmUnsavedChanges = async (onDiscard) => {
        const result = await Swal.fire({
            title: t('medical_history.unsaved_changes_title', 'Unsaved Changes'),
            text: t('medical_history.unsaved_changes_text', 'You have unsaved changes. What would you like to do?'),
            icon: 'warning',
            showCancelButton: true,
            showDenyButton: true,
            confirmButtonText: t('medical_history.save_and_continue', 'Save & Continue'),
            denyButtonText: t('medical_history.discard_changes', 'Discard'),
            cancelButtonText: t('medical_history.stay', 'Stay'),
            confirmButtonColor: '#4ABDBD',
            denyButtonColor: '#d33',
            cancelButtonColor: '#9ca3af',
            allowOutsideClick: false,
        });

        if (result.isConfirmed) {
            // Save all changed notes before navigating
            const original = originalNotesRef.current;
            for (const section of Object.keys(notes)) {
                if (notes[section] !== (original[section] || '') && notes[section].trim()) {
                    await handleSaveNotes(section);
                }
            }
            if (onDiscard) onDiscard();
        } else if (result.isDenied) {
            // Discard changes — restore original values
            setNotes({ ...originalNotesRef.current });
            if (onDiscard) onDiscard();
        }
        // Cancel — stay, do nothing
    };

    // Doctor-only: when true, the Completed tab right-section becomes fully editable
    const [isEditingCompleted, setIsEditingCompleted] = useState(false);

    // Reset edit mode whenever the user switches tabs or selects a different appointment
    useEffect(() => {
        setIsEditingCompleted(false);
    }, [selectedTab, selectedCard?.appointmentId]);

    // Determine if inputs should be disabled
    const isInputDisabled = () => {
        if (isSaving || patientLoading || appointmentLoading) {
            return true;
        }
        if (userRole === 'patient' || userRole === 'admin') {
            return true;
        }
        if (userRole === 'doctor') {
            if (selectedTab === 'Booked') return false;
            if (selectedTab === 'Completed') return !isEditingCompleted;
            return true;
        }
        return false;
    };

    // Disable a save button only when its own save is in flight (or due to
    // permission/page-load reasons). This prevents every save button from
    // appearing "loading" when one is clicked.
    const isSaveButtonDisabled = (fieldKey) => {
        if (patientLoading || appointmentLoading) return true;
        if (savingField !== null && savingField !== fieldKey) return false;
        if (savingField === fieldKey) return true;
        if (userRole === 'patient' || userRole === 'admin') return true;
        if (userRole === 'doctor') {
            if (selectedTab === 'Booked') return false;
            if (selectedTab === 'Completed') return !isEditingCompleted;
            return true;
        }
        return false;
    };

    // Fetch medical history when patientId becomes available or when tab changes
    useEffect(() => {
        // Get patientId from selectedPatient or localStorage (for patient role)
        const patientId = selectedPatient?.patientId || (userRole === 'patient' ? localStorage.getItem('ID') : null);

        // Skip if already fetching to prevent concurrent calls
        if (isFetchingRef.current) {
            console.log('MedicalHistory - Skipping: API call already in progress');
            return;
        }

        // Check if patientId or tab has changed
        const patientIdChanged = patientId && patientId !== previousPatientIdRef.current;
        const tabChanged = previousTabRef.current !== null && selectedTab !== previousTabRef.current;

        // Determine if we should fetch:
        // 1. On initial mount: only if patientId is available and we haven't fetched yet
        // 2. After initial mount: only if patientId changed (from null to value) or tab actually changed
        const shouldFetch = patientId && (
            (isInitialMountRef.current && !hasInitialFetchRef.current) || // Initial mount with patientId available
            (!isInitialMountRef.current && patientIdChanged) || // PatientId became available after mount
            (!isInitialMountRef.current && tabChanged) // Tab changed after initial mount
        );

        if (shouldFetch) {
            console.log('MedicalHistory - Calling getMedicalHistory API with patientId:', patientId, 'status:', selectedTab, 'isInitialMount:', isInitialMountRef.current);

            // Update refs synchronously BEFORE dispatch to prevent duplicate calls
            if (patientId) {
                previousPatientIdRef.current = patientId;
            }
            previousTabRef.current = selectedTab;
            hasInitialFetchRef.current = true;
            isInitialMountRef.current = false;
            isFetchingRef.current = true; // Set flag to prevent concurrent calls

            // Fetch medical history when patient becomes available or tab changes
            dispatch(getMedicalHistory({
                patientId: patientId,
                status: selectedTab,
                page: 1,
                limit: 5
            })).finally(() => {
                isFetchingRef.current = false; // Reset flag after API call completes
                setIsSwitchingTab(false);
                console.log('MedicalHistory - API call completed');
            });
        }
    }, [selectedPatient?.patientId, selectedTab, dispatch, userRole]);

    // Initial call to handleCardClick if selected has data
    useEffect(() => {
        if (selected && !hasCalledHandleCardClick && Object.keys(selected).length > 0) {
            handleCardClick(selected);
            setHasCalledHandleCardClick(true); // Prevent further calls
        }
    }, [selected, hasCalledHandleCardClick]);

    // Infinite scroll implementation
    useEffect(() => {
        if (!scrollObserverRef.current) return;

        const observer = new IntersectionObserver(
            (entries) => {
                const [entry] = entries;
                const patientId = selectedPatient?.patientId;
                if (
                    entry.isIntersecting &&
                    !medicalHistoryLoading &&
                    medicalHistoryPage < medicalHistoryTotalPages &&
                    patientId
                ) {
                    // Load next page with limit 5
                    dispatch(getMedicalHistory({
                        patientId: patientId,
                        status: selectedTab,
                        page: medicalHistoryPage + 1,
                        limit: 5
                    }));
                }
            },
            {
                root: null, // Use viewport as root for better mobile experience
                rootMargin: '200px', // Start loading 200px before reaching the bottom
                threshold: 0.1
            }
        );

        const currentObserverRef = scrollObserverRef.current;
        if (currentObserverRef) {
            observer.observe(currentObserverRef);
        }

        return () => {
            if (currentObserverRef) {
                observer.unobserve(currentObserverRef);
            }
        };
    }, [medicalHistoryLoading, medicalHistoryPage, medicalHistoryTotalPages, selectedTab, selectedPatient?.patientId, dispatch]);

    useEffect(() => {
        const handleOutsideClick = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setOpenMenuIndex(null);
            }
        };

        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    useEffect(() => {
        return () => {
            if (audioPreview) URL.revokeObjectURL(audioPreview);
            if (imagePreview) URL.revokeObjectURL(imagePreview);
        };
    }, [audioPreview, imagePreview]);

    // Fetch the doctor's configured prescription fields whenever the appointment
    // (and therefore the owning doctor) changes. The right-section renders these.
    useEffect(() => {
        const doctorId = selectedAppointment?.doctorId;
        if (doctorId) {
            dispatch(getPrescriptionFields(doctorId));
        }
    }, [selectedAppointment?.doctorId, dispatch]);

    useEffect(() => {
        if (!selectedAppointment) return;
        // Build a notes object keyed by every configured 'note' field. Read
        // from customNotes first, then fall back to the legacy appointment
        // column for the five seeded fields so historical data still appears.
        const next = {};
        prescriptionFields
            .filter((f) => f.kind !== 'static')
            .forEach((f) => {
                const stored = selectedAppointment.customNotes?.[f.fieldId];
                if (stored !== undefined && stored !== null && stored !== '') {
                    next[f.fieldId] = stored;
                    return;
                }
                const legacyCol = LEGACY_KEY_TO_COLUMN[f.legacyKey];
                next[f.fieldId] = legacyCol ? (selectedAppointment[legacyCol] || '') : '';
            });
        setNotes(next);
        originalNotesRef.current = { ...next };
    }, [selectedAppointment, prescriptionFields]);

    const isWithinTwoHoursBeforeAppointment = () => {
        // If early open restriction is disabled, always allow
        const restriction = selectedDoctor?.doctorSettings?.earlyOpenRestriction ?? doctorSettings?.earlyOpenRestriction;
        if (restriction === false) return true;

        const duration = selectedDoctor?.doctorSettings?.duration || doctorSettings?.duration;
        if (!selectedAppointment?.localTime || !duration) return false;

        const appointmentTime = new Date(selectedAppointment.localTime);
        const currentTime = new Date();
        const twoHoursBeforeAppointment = new Date(appointmentTime.getTime() - (duration * 60 * 1000));

        return currentTime >= twoHoursBeforeAppointment;
    };

    // Helper function to get the Monday of the current week
    const getCurrentWeekMonday = (date) => {
        const day = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
        const diff = date.getDate() - day + (day === 0 ? -6 : 1);
        return new Date(date.getFullYear(), date.getMonth(), diff);
    };

    // Helper function to scroll to bottom of details section
    const scrollDetailsToBottom = () => {
        setTimeout(() => {
            if (detailsContainerRef.current) {
                detailsContainerRef.current.scrollTop = detailsContainerRef.current.scrollHeight;
            }
        }, 0);
    };

    // Handle View More Slots button click
    const handleViewMoreSlotsInHistory = (recommendation) => {
        setIsViewingMoreSlots(true);
        setCurrentRecommendation(recommendation);
        setWeekOffsetMore(0);
        setOpenDayMore(null);

        // Fetch available slots
        const today = new Date();
        const currentWeekMonday = getCurrentWeekMonday(today);
        const startDate = format(addDays(currentWeekMonday, 0), 'yyyy-MM-dd');
        const endDate = format(addDays(new Date(startDate), 6), 'yyyy-MM-dd');

        if (userRole === 'patient') {
            dispatch(getAvailableSlots({
                doctorId: recommendation.doctorId,
                startDate,
                endDate,
                timeZone: systemTimeZone,
                addressType: recommendation.addressType,
                appointmentTypeId: typeof recommendation.appointmentType === 'object' ? recommendation.appointmentType?.typeId : recommendation.appointmentType,
                duration: recommendation.duration,
                isReschedulingProcess: true,
                appointmentId: recommendation.appointmentId,
            }));
        } else {
            dispatch(getDoctorAvailableSlots({
                doctorId: recommendation.doctorId,
                startDate,
                endDate,
                timeZone: systemTimeZone,
                addressType: recommendation.addressType,
                appointmentTypeId: typeof recommendation.appointmentType === 'object' ? recommendation.appointmentType?.typeId : recommendation.appointmentType,
                duration: recommendation.duration,
                isReschedulingProcess: true,
                appointmentId: recommendation.appointmentId,
            }));
        }

        // Scroll to bottom to show available slots
        scrollDetailsToBottom();
    };

    // Handle direct slot click for rescheduling in medical history
    const handleDirectSlotClickInHistory = async (slot, recommendation) => {
        const slotDateTime = new Date(slot.start);

        // Check if the selected slot is in the past
        if (slotDateTime <= new Date()) {
            toast.warn(t('medical_history.slot_passed_text'));
            return;
        }

        const result = await Swal.fire({
            title: t('medical_history.confirm_reschedule_title', { defaultValue: t('patientappointment.confirm_reschedule_title') }),
            text: t('medical_history.confirm_reschedule_text', { date: format(slotDateTime, 'MMM d, yyyy h:mm a'), defaultValue: `Are you sure you want to reschedule to ${format(slotDateTime, 'MMM d, yyyy h:mm a')}?` }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('medical_history.yes_reschedule_button') || t('patientappointment.yes_reschedule_button'),
            cancelButtonText: t('medical_history.cancel_button') || t('patientappointment.cancel_button')
        });

        if (result.isConfirmed) {
            try {
                const date = format(slotDateTime, 'yyyy-MM-dd');
                const time = format(slotDateTime, 'HH:mm');

                // Calculate end time based on duration from the recommendation (original appointment)
                const selectedApptType = selectedDoctor?.appointmentSpeciality
                    ?.find(spec => spec.name === recommendation.specialtyName)
                    ?.appointmentTypes?.find(type => type.type === recommendation.specialtyTypeName || type.type === recommendation.appointTypeName);

                const duration = recommendation.duration || selectedApptType?.duration || '1:00';
                const durationStr = typeof duration === 'number' ? `${Math.floor(duration / 60)}:${duration % 60}` : String(duration);
                const [hours, minutes] = durationStr.split(':').map(Number);
                const slotEndTime = new Date(slotDateTime.getTime() + (hours * 60 + minutes) * 60 * 1000);
                const slotEndTimeStr = format(slotEndTime, 'HH:mm');

                const patientId = selectedPatient?.patientId || localStorage.getItem('ID');

                const payload = {
                    appointmentId: recommendation.appointmentId,
                    patientId: patientId,
                    doctorId: recommendation.doctorId,
                    newDate: date,
                    newTime: time,
                    newSlotEndTime: slotEndTimeStr,
                    timeZone: systemTimeZone,
                    appointmentTypeId: selectedApptType?.typeId || (typeof recommendation.appointmentType === 'object' ? recommendation.appointmentType?.typeId : recommendation.appointmentType),
                    description: recommendation.description || '',
                    doctorDescription: recommendation.doctorDescription || null,
                    appointmentMode: recommendation.appointmentMode || 'onsite',
                    SpecialtyId: selectedApptType?.specialtyId || recommendation.SpecialtyId,
                    addressType: recommendation.addressType,
                    duration: durationStr,
                };

                const actionResult = await dispatch(rescheduleAppointment(payload));

                if (actionResult.meta.requestStatus === 'fulfilled') {
                    setShowDetails(false);
                    setCurrentRecommendation(null);
                    setIsViewingMoreSlots(false);
                    setWeekOffsetMore(0);
                    setOpenDayMore(null);

                    toast.success(t('medical_history.reschedule_success_text') || 'Appointment rescheduled successfully!');

                    // Refresh medical history
                    dispatch(getMedicalHistory({
                        patientId: patientId,
                        status: 'Rescheduling',
                        page: 1,
                        limit: 5
                    }));
                } else {
                    throw new Error(extractErrorMessage(actionResult.payload, t('medical_history.reschedule_error_text') || 'Failed to reschedule', i18n.language));
                }
            } catch (err) {
                toast.warn(extractErrorMessage(err, t('medical_history.reschedule_error_text') || 'Failed to reschedule appointment', i18n.language));
            }
        }
    };

    // Handle week navigation for view more slots in medical history
    const handleNextWeekMore = () => {
        setWeekOffsetMore(prev => Math.min(2, prev + 1));
        setOpenDayMore(null);
    };

    const handlePrevWeekMore = () => {
        setWeekOffsetMore(prev => Math.max(0, prev - 1));
        setOpenDayMore(null);
    };

    // Toggle day drawer for view more slots in medical history
    const toggleDayDrawerMore = (day) => {
        setOpenDayMore(openDayMore === day ? null : day);
    };

    // Fetch slots when week navigation changes (prev/next week buttons)
    useEffect(() => {
        if (isViewingMoreSlots && currentRecommendation) {
            const today = new Date();
            const currentWeekMonday = getCurrentWeekMonday(today);
            const startDate = format(addDays(currentWeekMonday, weekOffsetMore * 7), 'yyyy-MM-dd');
            const endDate = format(addDays(new Date(startDate), 6), 'yyyy-MM-dd');

            if (userRole === 'patient') {
                dispatch(getAvailableSlots({
                    doctorId: currentRecommendation.doctorId,
                    startDate,
                    endDate,
                    timeZone: systemTimeZone,
                    addressType: currentRecommendation.addressType,
                    appointmentTypeId: typeof currentRecommendation.appointmentType === 'object' ? currentRecommendation.appointmentType?.typeId : currentRecommendation.appointmentType,
                    duration: currentRecommendation.duration,
                    isReschedulingProcess: true,
                    appointmentId: currentRecommendation.appointmentId,
                }));
            } else {
                dispatch(getDoctorAvailableSlots({
                    doctorId: currentRecommendation.doctorId,
                    startDate,
                    endDate,
                    timeZone: systemTimeZone,
                    addressType: currentRecommendation.addressType,
                    appointmentTypeId: typeof currentRecommendation.appointmentType === 'object' ? currentRecommendation.appointmentType?.typeId : currentRecommendation.appointmentType,
                    duration: currentRecommendation.duration,
                    isReschedulingProcess: true,
                    appointmentId: currentRecommendation.appointmentId,
                }));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weekOffsetMore]);

    const toggleMenu = (index) => {
        setOpenMenuIndex(openMenuIndex === index ? null : index);
    };

    const handleAudioChange = (e) => {
        if (isInputDisabled()) return;
        const file = e.target.files[0];

        if (!file) {
            setAudioError('');
            return;
        }

        // Validate file type
        if (!file.type.startsWith("audio/")) {
            setAudioError(t("medical_history.invalid_audio_alert"));
            return;
        }

        // Validate file size
        if (file.size > MAX_AUDIO_SIZE) {
            setAudioError(t('medicalHistory.audio_size_exceed'));
            return;
        }

        setAudioError('');
        setAudioFile(file);
        setAudioPreview(URL.createObjectURL(file));
    };

    // Doctor flow: collect payment and complete the appointment. Payment is
    // mandatory to complete — there is no "pay later".
    const handleCompleteClick = () => {
        const docId = selectedAppointment?.doctorId;
        if (docId) dispatch(getPaymentOptions(docId));
        setPaymentModalMode('complete');
        // Default to the method picked at booking, else cash.
        setPaymentMethod(selectedAppointment?.payment?.method || 'cash');
        setShowPaymentModal(true);
    };

    // Patient flow: settle the appointment online/card at any time before it's
    // completed. Cash is collected in person by the doctor, so it's not offered.
    const handlePayClick = () => {
        const docId = selectedAppointment?.doctorId;
        if (docId) dispatch(getPaymentOptions(docId));
        setPaymentModalMode('pay');
        const booked = selectedAppointment?.payment?.method;
        const selfPayable = booked && booked !== 'cash' && booked !== 'paylater';
        setPaymentMethod(selfPayable ? booked : PATIENT_PAY_METHODS[0].key);
        setShowPaymentModal(true);
    };

    // The invoice modal IS the confirmation now (no extra popup). In 'complete'
    // mode the doctor records payment + completes; in 'pay' mode the patient just
    // settles the payment (the appointment stays Booked until the doctor completes).
    const handlePaymentSubmit = () => {
        const appointmentId = selectedCard.appointmentId || '';
        const patientId = selectedPatient?.patientId || (userRole === 'patient' ? localStorage.getItem('ID') : null);
        const refreshList = () => {
            if (!patientId) return;
            isFetchingRef.current = true;
            dispatch(getMedicalHistory({ patientId, status: selectedTab, page: 1, limit: 5 }))
                .finally(() => { isFetchingRef.current = false; });
        };

        setSavingField('payment');

        // Patient early-payment: record the payment, do NOT complete the appointment.
        if (paymentModalMode === 'pay') {
            dispatch(payAppointment({ appointmentId, method: paymentMethod }))
                .unwrap()
                .then(() => {
                    setShowPaymentModal(false);
                    toast.success(t('medical_history.payment_recorded_text', 'Payment recorded successfully!'));
                    // Refresh the open appointment so its "Paid" badge appears.
                    if (appointmentId) dispatch(getAppointmentById(appointmentId));
                    refreshList();
                })
                .catch((error) => {
                    toast.warn(error || t('medical_history.payment_error_text'));
                })
                .finally(() => {
                    setSavingField(null);
                });
            return;
        }

        // Doctor completion: payment must be collected to complete (Paid, always).
        dispatch(updateAppointment({
            appointmentId,
            method: paymentMethod,
            paymentStatus: 'Paid',
            date: new Date().toISOString(),
            appointmentStatus: 'Completed',
        }))
            .unwrap()
            .then(() => {
                setShowPaymentModal(false);
                setShowDetails(false);
                toast.success(t('medical_history.payment_success_regular_text'));
                // Refresh the appointment list to drop the completed appointment from Booked.
                if (selectedTab === 'Booked') refreshList();
            })
            .catch((error) => {
                toast.warn(error || t('medical_history.payment_error_text'));
            })
            .finally(() => {
                setSavingField(null);
            });
    };

    const handleImageChange = (e) => {
        if (isInputDisabled()) return;
        const file = e.target.files[0];

        if (!file) {
            setImageError('');
            return;
        }

        // Validate file type
        if (!file.type.startsWith('image/')) {
            setImageError(t('medical_history.invalid_image_alert'));
            return;
        }

        // Validate file size
        if (file.size > MAX_IMAGE_SIZE) {
            setImageError(t('medicalHistory.image_size_exceed'));
            return;
        }

        setImageError('');
        setImageFile(file);
        setImagePreview(URL.createObjectURL(file));
    };

    const handleCardClick = async (item) => {
        // Check for unsaved changes before switching
        if (hasUnsavedChanges() && selectedCard?.appointmentId && selectedCard.appointmentId !== item.appointmentId) {
            await confirmUnsavedChanges(() => {
                proceedCardClick(item);
            });
            return;
        }
        proceedCardClick(item);
    };

    const proceedCardClick = async (item) => {
        console.log('item', item);
        const { date, time } = formatDateTime(item.localTime);
        setSelectedCard({
            speciality: item.specialtyName,
            // Carry the specialtyId alongside the snapshot name so the detail panel
            // header can resolve the translated label via <MasterLabel>.
            specialtyId: item.SpecialtyId,
            type: item.specialtyTypeName,
            color: item.colour,
            date,
            time,
            appointmentId: item.appointmentId
        });
        setShowDetails(true);
        setIsCardLoading(true);
        setViewingImageUpload(null);

        // Store recommendation data if Rescheduling tab (for all roles to view)
        if (selectedTab === 'Rescheduling') {
            setCurrentRecommendation(item);
        }

        try {
            const promises = [dispatch(getAppointmentById(item.appointmentId)), new Promise(resolve => setTimeout(resolve, 1000))];
            // Fetch doctor settings for patient role in Booked tab to determine Connect Meet button visibility
            if (userRole === 'patient' && selectedTab === 'Booked' && item.doctorId) {
                promises.push(dispatch(getDoctorSettings(item.doctorId)));
            }
            const result = await Promise.all(promises);
            setIsCardLoading(false);

            // If Rescheduling tab, merge appointment data with recommendation data
            if (selectedTab === 'Rescheduling' && result[0]?.payload) {
                setCurrentRecommendation(prev => ({
                    ...prev,
                    ...result[0].payload,
                    ...item // Keep original item data
                }));
            }
        } catch (error) {
            setIsCardLoading(false);
            toast.warn(t('medical_history.fetch_error_message'));
        }
    };

    const handleBackClick = async () => {
        if (hasUnsavedChanges()) {
            await confirmUnsavedChanges(() => {
                setShowDetails(false);
                setCurrentRecommendation(null);
            });
            return;
        }
        setShowDetails(false);
        setCurrentRecommendation(null);
    };

    // Save and restore scroll position for right panel
    const saveScrollPosition = () => {
        if (detailsContainerRef.current) {
            scrollPositionRef.current = detailsContainerRef.current.scrollTop;
            shouldRestoreScrollRef.current = true;
        }
    };
    const restoreScrollPosition = () => {
        shouldRestoreScrollRef.current = true;
    };

    // Track scroll position continuously so we always have the latest
    const handleDetailsScroll = () => {
        if (detailsContainerRef.current && !shouldRestoreScrollRef.current) {
            scrollPositionRef.current = detailsContainerRef.current.scrollTop;
        }
    };

    // Restore scroll using MutationObserver to detect when DOM updates complete
    useEffect(() => {
        const container = detailsContainerRef.current;
        if (!container) return;

        const observer = new MutationObserver(() => {
            if (shouldRestoreScrollRef.current && scrollPositionRef.current > 0) {
                container.scrollTop = scrollPositionRef.current;
            }
        });

        observer.observe(container, { childList: true, subtree: true });

        return () => observer.disconnect();
    }, [showDetails, selectedAppointment]);

    // Also restore on every render as fallback
    useEffect(() => {
        if (shouldRestoreScrollRef.current && detailsContainerRef.current && scrollPositionRef.current > 0) {
            requestAnimationFrame(() => {
                if (detailsContainerRef.current) {
                    detailsContainerRef.current.scrollTop = scrollPositionRef.current;
                    // Keep the flag true for a short time to handle multiple re-renders
                    setTimeout(() => {
                        shouldRestoreScrollRef.current = false;
                    }, 500);
                }
            });
        }
    });

    const handleSaveNotes = async (section) => {
        if (isInputDisabled()) return;
        saveScrollPosition();
        const appointmentId = selectedCard.appointmentId;
        const doctorId = selectedAppointment?.doctorId;
        const noteContent = notes[section];

        // Validate notes content
        const error = validateNotesInput(noteContent, t);
        if (error) {
            setNotesErrors((prev) => ({
                ...prev,
                [section]: error
            }));
            return;
        }

        if (!noteContent.trim()) {
            alert(t('medical_history.empty_notes_error'));
            return;
        }

        // Clear error if valid
        setNotesErrors((prev) => ({
            ...prev,
            [section]: ''
        }));

        setSavingField(`notes:${section}`);
        try {
            const result = await dispatch(updateAppointmentCustomNote({
                appointmentId,
                fieldId: section,
                value: noteContent,
            }));

            if (result.meta.requestStatus === 'fulfilled') {
                toast.success(t('medical_history.save_success_message'));
                originalNotesRef.current = { ...originalNotesRef.current, [section]: noteContent };
            } else {
                toast.warn(result.payload || t('medical_history.save_error_message'));
            }
        } catch (error) {
            toast.warn(t('medical_history.save_error_message'));
        } finally {
            setSavingField(null);
            restoreScrollPosition();
        }
    };

    const handleSaveAudio = async () => {
        if (isInputDisabled()) return;
        saveScrollPosition();
        setSavingField('audio');
        try {
            const result = await dispatch(
                uploadAudio({
                    appointmentId: selectedCard.appointmentId,
                    audio: audioFile,
                })
            );

            if (result.meta.requestStatus === "fulfilled") {
                toast.success(t("medical_history.audio_upload_success_message"));
                setAudioFile(null);
                setAudioPreview(null);
            } else {
                toast.warn(result.payload || t("medical_history.audio_upload_error_message"));
            }
        } catch (error) {
            toast.warn(t("medical_history.audio_upload_error_message"));
        } finally {
            setSavingField(null);
            restoreScrollPosition();
        }
    };

    const handleSaveImage = async () => {
        if (isInputDisabled()) return;
        saveScrollPosition();
        if (!imageFile) {
            alert(t('medical_history.no_image_selected'));
            return;
        }
        const currentUploads = selectedAppointment?.imageUploads || [];
        if (currentUploads.length >= 5) {
            toast.warn(t('medical_history.max_images_text', 'Maximum 5 images allowed per appointment.'));
            return;
        }
        setSavingField('image');
        try {
            const result = await dispatch(uploadImage({ appointmentId: selectedCard.appointmentId, image: imageFile }));
            if (result.meta.requestStatus === 'fulfilled') {
                toast.success(t('medical_history.image_upload_success_message'));
                setImageFile(null);
                setImagePreview(null);
            } else {
                const errorMsg = result.payload?.message?.en || result.payload?.message || t('medical_history.image_upload_error');
                toast.warn(errorMsg);
            }
        } catch (error) {
            alert(t('medical_history.image_upload_error'));
        } finally {
            setSavingField(null);
            restoreScrollPosition();
        }
    };

    const handleDeleteImage = async (uploadId) => {
        saveScrollPosition();
        const result = await Swal.fire({
            title: t('medical_history.delete_image_title', 'Remove Image'),
            text: t('medical_history.delete_image_text', 'Are you sure you want to remove this image and its OCR notes?'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#9ca3af',
            confirmButtonText: t('medical_history.delete_confirm', 'Remove'),
            cancelButtonText: t('medical_history.delete_cancel', 'Cancel'),
        });
        if (result.isConfirmed) {
            try {
                await dispatch(deleteUploadedImage({ appointmentId: selectedCard.appointmentId, uploadId })).unwrap();
                toast.success(t('medical_history.image_deleted_text', 'Image removed successfully.'));
            } catch (error) {
                toast.warn(t('medical_history.image_delete_error', 'Failed to remove image.'));
            }
        }
        restoreScrollPosition();
    };

    const handleDeleteAudioUpload = async (uploadId) => {
        if (!uploadId) return;
        saveScrollPosition();
        const result = await Swal.fire({
            title: t('medical_history.delete_audio_title', 'Remove audio?'),
            text: t('medical_history.delete_audio_text', 'Are you sure you want to remove this audio clip and its transcript?'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            cancelButtonColor: '#9ca3af',
            confirmButtonText: t('medical_history.delete_confirm', 'Remove'),
            cancelButtonText: t('medical_history.delete_cancel', 'Cancel'),
        });
        if (result.isConfirmed) {
            try {
                await dispatch(deleteAudio({ appointmentId: selectedCard.appointmentId, uploadId })).unwrap();
                toast.success(t('medical_history.audio_deleted_text', 'Audio removed successfully.'));
            } catch (error) {
                toast.warn(t('medical_history.audio_delete_error', 'Failed to remove audio.'));
            }
        }
        restoreScrollPosition();
    };

    const handleCancel = async (data) => {
        try {
            const confirmResult = await Swal.fire({
                title: t('medical_history.cancel_confirm_title'),
                text: t('medical_history.cancel_confirm_text'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonColor: '#4ABDBD',
                cancelButtonColor: '#3085d6',
                confirmButtonText: t('medical_history.cancel_confirm_button'),
                cancelButtonText: t('medical_history.cancel_cancel_button')
            });

            if (confirmResult.isConfirmed) {
                const result = await dispatch(cancelAppointment({ appointmentId: data.appointmentId, doctorId: data.doctorId }));

                if (result.meta.requestStatus === 'fulfilled') {
                    setOpenMenuIndex(null);
                    dispatch(getPatientById(data.patientId));
                    toast.success(t('medical_history.cancel_success_message'));
                } else {
                    toast.warn(result.payload || t('medical_history.cancel_error_message'));
                }
            }
        } catch (error) {
            toast.warn(t('medical_history.cancel_error_message'));
        }
    };

    const handleReschedule = async (data) => {
        if (userRole === 'patient') {
            navigate('/patientAppointment', { state: { reschedulestate: data } });
        }
    };

    // Handle slot selection from recommendation (for Rescheduling tab)
    const handleRecommendationSlotClick = async (slotDateTimeString, recommendation) => {

        const slotDateTime = new Date(slotDateTimeString);

        // Check if the selected slot is in the past
        if (slotDateTime <= new Date()) {
            toast.warn(t('medical_history.slot_passed_text'));
            return;
        }

        const result = await Swal.fire({
            title: t('patientappointment.confirm_reschedule_title'),
            text: t('patientappointment.confirm_reschedule_text', { date: format(slotDateTime, 'MMM d, yyyy h:mm a') }),
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
            confirmButtonText: t('patientappointment.yes_reschedule_button'),
            cancelButtonText: t('patientappointment.cancel_button')
        });

        if (result.isConfirmed) {
            try {
                const selectedApptType = selectedDoctor?.appointmentSpeciality
                    ?.find(spec => spec.name === recommendation.specialtyName)
                    ?.appointmentTypes?.find(type => type.type === recommendation.specialtyTypeName || type.type === recommendation.appointTypeName);

                const date = format(slotDateTime, 'yyyy-MM-dd');
                const time = format(slotDateTime, 'HH:mm');

                // Calculate end time based on duration from the recommendation (original appointment)
                const duration = recommendation.duration || selectedApptType?.duration || '1:00';
                const durationStr = typeof duration === 'number' ? `${Math.floor(duration / 60)}:${duration % 60}` : String(duration);
                const [hours, minutes] = durationStr.split(':').map(Number);
                const slotEndTime = new Date(slotDateTime.getTime() + (hours * 60 + minutes) * 60 * 1000);
                const slotEndTimeStr = format(slotEndTime, 'HH:mm');

                const patientId = selectedPatient?.patientId || localStorage.getItem('ID');

                const payload = {
                    appointmentId: recommendation.appointmentId,
                    patientId: patientId,
                    doctorId: recommendation.doctorId,
                    newDate: date,
                    newTime: time,
                    newSlotEndTime: slotEndTimeStr,
                    timeZone: systemTimeZone,
                    appointmentTypeId: selectedApptType?.typeId || (typeof recommendation.appointmentType === 'object' ? recommendation.appointmentType?.typeId : recommendation.appointmentType),
                    description: recommendation.description || '',
                    doctorDescription: recommendation.doctorDescription || null,
                    appointmentMode: recommendation.appointmentMode || 'onsite',
                    SpecialtyId: selectedApptType?.specialtyId || recommendation.SpecialtyId,
                    addressType: recommendation.addressType,
                    duration: durationStr,
                };

                const actionResult = await dispatch(rescheduleAppointment(payload));

                if (actionResult.meta.requestStatus === 'fulfilled') {
                    setShowDetails(false);
                    setCurrentRecommendation(null);
                    // Refresh medical history
                    dispatch(getMedicalHistory({
                        patientId: patientId,
                        status: 'Rescheduling',
                        page: 1,
                        limit: 5
                    }));

                    toast.success(t('patientappointment.reschedule_success_text'));
                } else {
                    throw new Error(extractErrorMessage(actionResult.payload, t('patientappointment.reschedule_error_text'), i18n.language));
                }
            } catch (err) {
                toast.warn(extractErrorMessage(err, t('patientappointment.reschedule_error_text'), i18n.language));
            }
        }
    };

    // Handle cancel appointment from recommendation (for Rescheduling tab)
    const handleCancelRecommendationAppointment = async (recommendation) => {

        const result = await Swal.fire({
            title: t('patientappointment.cancel_appointment_confirm_title'),
            text: t('patientappointment.cancel_appointment_confirm_text'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#4ABDBD',
            confirmButtonText: t('patientappointment.yes_cancel_button'),
            cancelButtonText: t('patientappointment.no_button')
        });

        if (result.isConfirmed) {
            try {
                const actionResult = await dispatch(cancelAppointment({
                    appointmentId: recommendation.appointmentId,
                    doctorId: recommendation.doctorId
                }));

                if (actionResult.meta.requestStatus === 'fulfilled') {
                    setShowDetails(false);
                    setCurrentRecommendation(null);
                    // Refresh medical history
                    const patientId = selectedPatient?.patientId || localStorage.getItem('ID');
                    dispatch(getMedicalHistory({
                        patientId: patientId,
                        status: 'Rescheduling',
                        page: 1,
                        limit: 5
                    }));

                    toast.success(t('patientappointment.cancel_success_text'));
                } else {
                    throw new Error(extractErrorMessage(actionResult.payload, t('patientappointment.cancel_error_text'), i18n.language));
                }
            } catch (err) {
                toast.warn(extractErrorMessage(err, t('patientappointment.cancel_error_text'), i18n.language));
            }
        }
    };

    const tabs = ['Booked', 'Rescheduling', 'Completed', 'Cancelled', 'NoShow'];

    const handleNavTab = async (tab) => {
        if (hasUnsavedChanges()) {
            await confirmUnsavedChanges(() => {
                proceedNavTab(tab);
            });
            return;
        }
        proceedNavTab(tab);
    };

    const proceedNavTab = (tab) => {
        setShowDetails(false);
        setIsSwitchingTab(true);
        setSelectedTab(tab);
        setSelectedCard({
            speciality: '',
            type: '',
            color: '#000000',
            date: '',
            time: '',
            appointmentId: ''
        });
        setCurrentRecommendation(null);
    };

    return (
        <div data-testid="medical-history" className="">
            <div className="flex gap-2 mb-6 flex-wrap items-center">
                <div data-testid="medical-history-tabs" className="flex gap-2 flex-wrap flex-1">
                    {tabs.map((tab) => (
                        <button
                            key={tab}
                            data-testid={`medical-history-tab-${tab.toLowerCase()}`}
                            className={`relative px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-300 w-fit text-center shadow-md hover:shadow-lg ${selectedTab === tab
                                ? 'text-white scale-105'
                                : 'bg-gradient-to-r from-gray-200 to-gray-300 text-gray-700 hover:from-gray-300 hover:to-gray-400 hover:scale-105 cursor-pointer'
                                }`}
                            onClick={() => handleNavTab(tab)}
                        >
                            {selectedTab === tab && (
                                <motion.div
                                    layoutId="activeMedicalHistoryStatusTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] rounded-xl shadow-md !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span className="relative z-20">{t(`medical_history.tabs.${tab.toLowerCase()}`)}</span>
                        </button>
                    ))}
                </div>

                {/* Right side buttons for Summary and Ask AI */}
                {(userRole === 'doctor' || userRole === 'admin') && !isOpenAppointmentMode && (
                    <div data-testid="medical-history-side-actions" className="flex gap-4 ml-auto">
                        <button
                            data-testid="medical-history-summary-button"
                            onClick={() => setIsSummaryDrawerOpen(true)}
                            className="bg-[#e2e6eb] text-[#2c3e50] font-semibold flex items-center gap-2 px-5 py-2.5 rounded-2xl shadow hover:bg-[#d1d8e0] transition-colors cursor-pointer"
                        >
                            <FileText size={18} />
                            {t('medicalHistory.summary')}
                        </button>
                        {/* <button
                            onClick={() => setIsAskAIDrawerOpen(true)}
                            className="bg-[#3A9B9B] text-white font-semibold flex items-center gap-2 px-5 py-2.5 rounded-2xl shadow hover:bg-[#2e7d7d] transition-colors cursor-pointer"
                        >
                            <MessageSquare size={18} />
                            {t('medicalHistory.ask_ai')}
                        </button> */}
                    </div>
                )}
            </div>

            {medicalHistoryLoading && (!medicalHistoryData || medicalHistoryData.length === 0) ? (
                <div data-testid="medical-history-initial-loading" className="flex flex-col items-center justify-center min-h-[50vh]">
                    <CommonLoader />
                    <span data-testid="medical-history-initial-loading-text" className="text-lg sm:text-xl text-[#4ABDBD] mt-4">{t('patientpage.loading')}</span>
                </div>
            ) : (!medicalHistoryData || medicalHistoryData.length === 0) ? (
                <div data-testid="medical-history-empty" className="flex flex-col items-center justify-center min-h-[50vh] text-center">
                    <img
                        data-testid="medical-history-empty-image"
                        src={emptymedicalhistory}
                        alt={t('medical_history.no_data_alt')}
                        className="w-2/3 sm:w-1/2 md:w-1/3 lg:w-1/4 max-w-[200px] mb-4"
                    />
                    <p data-testid="medical-history-empty-message" className="text-lg font-semibold text-gray-600">
                        {t('medical_history.no_data_message')}
                    </p>
                    {userRole === 'patient' && (
                        <>
                            <p data-testid="medical-history-empty-submessage" className="text-sm text-gray-500 mt-2">
                                {t('medical_history.no_data_submessage')}
                            </p>
                            <button
                                data-testid="medical-history-book-appointment-button"
                                onClick={() => navigate("/patientAppointment")}
                                className="mt-4 px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                            >
                                {t('medical_history.go_to_appointment')}
                            </button>
                        </>
                    )}
                </div>
            ) : (
                <div className="flex flex-col lg:flex-row gap-4">
                    {/* Cards Section */}
                    <div
                        ref={cardsContainerRef}
                        className={`relative w-full lg:w-80 flex-shrink-0 overflow-y-auto custom-scrollbargray pr-4 lg:pr-6 border-r border-gray-200 max-h-[calc(100vh-200px)] ${showDetails ? 'hidden lg:block' : 'block'
                            }`}
                    >
                        {/* Loading overlay when switching tabs (not for pagination) */}
                        {isSwitchingTab && medicalHistoryLoading && (
                            <div className="absolute inset-0 bg-white/80 backdrop-blur-sm z-20 flex flex-col items-center justify-center rounded-xl">
                                <CommonLoader />
                                <span className="text-sm sm:text-base text-[#4ABDBD] mt-3 font-semibold">{t('patientpage.loading')}</span>
                            </div>
                        )}

                        {medicalHistoryData && medicalHistoryData.map((item, index) => {
                            const { date, time } = formatDateTime(item.localTime);
                            const showMenu = (userRole === 'patient' || userRole === 'doctor') && selectedTab === 'Booked';
                            const isSelected = selectedCard.appointmentId === item.appointmentId;
                            return (
                                <div
                                    key={item._id || index}
                                    data-testid={`medical-history-card-${item.appointmentId || item._id || index}`}
                                    className={`group relative flex flex-col p-5 pl-6 bg-gray-50 border border-gray-100 m-1 rounded-2xl shadow-sm mb-4 cursor-pointer transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg ${isSelected ? 'shadow-lg ring-1 ring-black/5' : ''}`}
                                    style={{ borderLeftWidth: '5px', borderLeftColor: item.colour || '#4ABDBD' }}
                                    onClick={(e) => {
                                        e.currentTarget.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                                        handleCardClick(item);
                                    }}
                                >
                                    {showMenu && (
                                        <button
                                            data-testid={`medical-history-card-menu-${item.appointmentId || item._id || index}`}
                                            className="absolute top-3 right-3 p-1 rounded-full text-gray-400 hover:text-[#3A9B9B] hover:bg-[#4ABDBD]/10 transition-colors focus:outline-none cursor-pointer"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleMenu(index);
                                            }}
                                        >
                                            <MoreVertical size={18} />
                                        </button>
                                    )}
                                    {openMenuIndex === index && showMenu && (
                                        <div
                                            ref={menuRef}
                                            className="absolute top-11 right-3 w-32 bg-white border border-gray-200 shadow-xl z-10 rounded-xl overflow-hidden"
                                        >
                                            {userRole === 'patient' && (
                                                <button
                                                    data-testid={`medical-history-reschedule-button-${item.appointmentId || index}`}
                                                    className="block w-full text-left px-4 py-2 text-sm bg-gradient-to-r from-yellow-500 to-yellow-600 text-white hover:from-yellow-600 hover:to-yellow-700 transition-all duration-300 font-semibold cursor-pointer"
                                                    onClick={() => handleReschedule(item)}
                                                >
                                                    {t('medical_history.reschedule_button')}
                                                </button>
                                            )}
                                            <button
                                                data-testid={`medical-history-cancel-button-${item.appointmentId || index}`}
                                                className="block w-full text-left px-4 py-2 text-sm text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 transition-all duration-300 font-semibold cursor-pointer"
                                                onClick={() => handleCancel(item)}
                                            >
                                                {t('medical_history.cancel_button')}
                                            </button>
                                        </div>
                                    )}
                                    <div className="flex items-center gap-2 pr-8">
                                        <CalendarCheck size={16} className="text-[#4ABDBD] shrink-0" />
                                        <p className="text-sm font-semibold text-slate-700 whitespace-nowrap tracking-tight">{date}</p>
                                        <span className="text-slate-300">|</span>
                                        <p className="text-sm font-medium text-slate-500 whitespace-nowrap tracking-tight">{time}</p>
                                    </div>
                                    <h3
                                        className="mt-3 inline-flex max-w-fit items-center text-sm sm:text-base font-semibold text-white truncate self-start px-3 py-1 rounded-full shadow-sm shadow-black/5"
                                        style={{ backgroundColor: item.colour || '#4ABDBD' }}
                                    >
                                        <MasterLabel type="masterSpecialty" id={item.SpecialtyId} field="name" fallback={item.specialtyName} />
                                    </h3>
                                    <p className="mt-2 text-sm font-medium text-slate-500 truncate pr-12">{item.specialtyTypeName}</p>
                                    {item.appointmentMode === 'visio' && (
                                        <Video
                                            size={30}
                                            strokeWidth={1.5}
                                            fill="#3A9B9B"
                                            color="#3A9B9B"
                                            className="absolute bottom-3 right-3 drop-shadow-sm"
                                        />
                                    )}
                                    {isSelected && (
                                        <div className="absolute -right-4.5 top-1/2 transform -translate-y-1/2 bg-gray-50 border-r border-t rotate-45 rounded-tr-md shadow-[1px_0px_0px_-0.5px_rgba(0,0,0,0.1)] border-gray-200 p-1" >
                                            <ChevronRight className="text-lg -rotate-45 text-[#3A9B9B]" />
                                        </div>
                                    )}
                                </div>
                            );
                        })}

                        {/* Infinite scroll trigger and loading indicator */}
                        {medicalHistoryPage < medicalHistoryTotalPages && (
                            <div ref={scrollObserverRef} className="flex justify-center items-center py-4">
                                {medicalHistoryLoading && (
                                    <div className="flex flex-col items-center gap-2">
                                        <div className="h-6 w-6 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></div>
                                        <p className="text-xs text-gray-500">{t('medical_history.loading_more')}</p>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* End of list indicator */}
                        {medicalHistoryPage >= medicalHistoryTotalPages && medicalHistoryData && medicalHistoryData.length > 0 && (
                            <div className="flex justify-center items-center py-4">
                                <p className="text-xs text-gray-400">{t('medical_history.end_of_list')}</p>
                            </div>
                        )}
                    </div>

                    {/* Details Section */}
                    <div
                        ref={detailsContainerRef}
                        onScroll={handleDetailsScroll}
                        className={`flex-1 overflow-y-auto custom-scrollbargray max-h-[calc(100vh-200px)] px-0 sm:px-4 lg:px-6 py-4 ${showDetails ? 'block' : 'hidden lg:block'
                            }`}
                    >
                        {/* Image Detail View */}
                        {viewingImageUploadLive ? (
                            <div className="flex flex-col gap-4">
                                <button
                                    type="button"
                                    data-testid="patient-medical-history-image-back-button"
                                    onClick={() => {
                                        setViewingImageUpload(null);
                                        setTimeout(() => {
                                            if (detailsContainerRef.current) {
                                                detailsContainerRef.current.scrollTop = scrollPositionRef.current;
                                            }
                                        }, 0);
                                    }}
                                    className="flex items-center gap-2 text-sm font-semibold text-[#4ABDBD] hover:text-[#3AA8A8] transition-colors duration-200 cursor-pointer w-fit"
                                >
                                    <ArrowLeft className="w-5 h-5" />
                                    {t('medical_history.back_to_notes', 'Back')}
                                </button>
                                <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                                    <img
                                        src={getFileURL(viewingImageUploadLive.imageUrl)}
                                        alt={t('medical_history.uploaded_image_alt')}
                                        className="w-full h-auto"
                                    />
                                </div>
                                {(viewingImageUploadLive.ocrNotes || viewingImageUploadLive.status === 'processing') && (
                                    <div>
                                        <p className="text-xs font-semibold text-[#4ABDBD] mb-1">
                                            {t('medical_history.ocr_notes_label')}
                                        </p>
                                        {viewingImageUploadLive.status === 'processing' ? (
                                            mediaTextBlock(viewingImageUploadLive.ocrNotes, 'processing', t('medical_history.generating_ocr', 'Generating OCR notes…'))
                                        ) : (
                                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">
                                                {viewingImageUploadLive.ocrNotes}
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        ) : showDetails && isCardLoading ? (
                            <div className="flex flex-col items-center justify-center min-h-[50vh]">
                                <CommonLoader />
                                <span className="text-lg sm:text-xl text-[#4ABDBD]">{t('patientpage.loading')}</span>
                            </div>
                        ) : showDetails && selectedAppointment ? (
                            <>
                                <button
                                    data-testid="patient-medical-history-back-mobile-button"
                                    className="lg:hidden mb-4 text-[#4ABDBD] flex items-center gap-2 cursor-pointer"
                                    onClick={handleBackClick}
                                >
                                    <ArrowLeft size={18} />
                                    <span className="text-sm sm:text-base font-semibold">
                                        {t('medical_history.back_button')}
                                    </span>
                                </button>
                                <div className="text-center mb-4 sm:mb-6 flex flex-col items-center">
                                    <span
                                        className="inline-block px-4 sm:px-5 py-1.5 sm:py-2 rounded-full text-base sm:text-lg md:text-xl font-bold text-white"
                                        style={{ backgroundColor: selectedCard.color }}
                                    >
                                        <MasterLabel type="masterSpecialty" id={selectedCard.specialtyId} field="name" fallback={selectedCard.speciality} />
                                    </span>
                                    <p className="text-sm sm:text-base font-semibold mt-2" style={{ color: selectedCard.color }}>
                                        {selectedCard.type}
                                    </p>
                                </div>
                                <div className="flex flex-wrap justify-center gap-3 sm:gap-4 md:gap-6 mb-4 sm:mb-6">
                                    {[
                                        { label: t('medical_history.date_label'), value: selectedCard.date },
                                        { label: t('medical_history.time_label'), value: selectedCard.time },
                                        {
                                            label: t('medical_history.status_label'),
                                            value: selectedAppointment.status && typeof selectedAppointment.status === 'string'
                                                ? (t(`medical_history.tabs.${selectedAppointment.status.toLowerCase()}`) || t('medical_history.status_booked_default'))
                                                : t('medical_history.status_booked_default'),
                                            className: 'text-green-600'
                                        },
                                        {
                                            label: t('medical_history.price_label'),
                                            value: selectedAppointment.price ? `${getCurrencySymbol(selectedAppointment.currency)}${selectedAppointment.price}` : t('medical_history.na'),
                                            priceType: selectedAppointment.pricetype === 'Type 1' ? t('appointmentdetails.price_type_appointment') :
                                                selectedAppointment.pricetype === 'Type 2' ? t('appointmentdetails.price_type_custom') :
                                                    selectedAppointment.pricetype === 'Type 3' ? t('appointmentdetails.price_type_manual') : null
                                        },
                                        {
                                            label: t('medical_history.mode_label'),
                                            value: selectedAppointment.appointmentMode === 'onsite' ? t(`appointmentdetails.Onsite`) : t('medicalHistory.visio')
                                        }
                                    ].map((item, index) => (
                                        <div key={index} className="flex items-center gap-2">
                                            <p className="text-sm sm:text-base font-semibold text-gray-500">{item.label}:</p>
                                            <p className={`text-sm sm:text-base font-semibold text-gray-400 ${item.className || ''}`}>
                                                {item.value}
                                            </p>
                                            {item.priceType && (userRole === 'doctor' || userRole === 'admin') && (
                                                <p className="text-[10px] sm:text-xs font-medium text-green-600 mt-0.5">
                                                    {item.priceType}
                                                </p>
                                            )}
                                            {item.label === t('medical_history.mode_label') && selectedAppointment.appointmentMode === 'visio' && !selectedAppointment.meetingCompleted && isWithinTwoHoursBeforeAppointment() && selectedTab === 'Booked' && (
                                                <button
                                                    data-testid="patient-medical-history-connect-meet-button"
                                                    className={`ml-4 px-4 py-2 font-semibold rounded-lg shadow-sm text-sm transition-all focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] focus:ring-opacity-50 flex items-center gap-2 ${(userRole === 'doctor' && selectedAppointment.doctorMeetingLink) || (userRole === 'patient' && (selectedAppointment.patientMeetingLink || selectedAppointment.doctorMeetingLink))
                                                        ? 'bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] text-white hover:from-[#3aa8a8] hover:to-[#2a7b7b] cursor-pointer'
                                                        : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                                        }`}
                                                    onClick={() => {
                                                        let meetingLink = null;
                                                        if (userRole === 'doctor') {
                                                            meetingLink = selectedAppointment.doctorMeetingLink;
                                                        } else if (userRole === 'patient') {
                                                            meetingLink = selectedAppointment.patientMeetingLink || selectedAppointment.doctorMeetingLink;
                                                        }

                                                        if (!meetingLink) return;

                                                        window.open(meetingLink, '_blank');
                                                    }}
                                                    disabled={!(userRole === 'doctor' && selectedAppointment.doctorMeetingLink) && !(userRole === 'patient' && (selectedAppointment.patientMeetingLink || selectedAppointment.doctorMeetingLink))}
                                                >
                                                    {(userRole === 'doctor' && selectedAppointment.doctorMeetingLink) || (userRole === 'patient' && (selectedAppointment.patientMeetingLink || selectedAppointment.doctorMeetingLink))
                                                        ? t('medicalHistory.connect_meet')
                                                        : t('medicalHistory.no_meeting_link')}
                                                </button>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                {/* Patient: settle the appointment (online/card) any time before it's
                                    completed; shows a "Paid" badge once settled. Placed in this common
                                    area so it renders for patients regardless of the doctor-only
                                    sections below (which patients don't render). */}
                                {userRole === 'patient' && selectedTab === 'Booked' && selectedAppointment.status === 'Booked' && (
                                    <div className="flex justify-center mb-4 sm:mb-6">
                                        {isPaidStatus(selectedAppointment.payment?.status) ? (
                                            <span data-testid="patient-medical-history-paid-badge" className="inline-flex items-center gap-1.5 px-5 py-2 rounded-xl bg-green-50 text-green-600 font-semibold text-sm sm:text-base">
                                                <CheckCircle2 className="w-4 h-4" />
                                                {t('payments.paid', 'Paid')}
                                            </span>
                                        ) : (
                                            <button
                                                type="button"
                                                data-testid="patient-medical-history-pay-button"
                                                className="px-5 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm sm:text-base cursor-pointer flex items-center justify-center gap-2"
                                                onClick={handlePayClick}
                                                disabled={patientLoading || appointmentLoading}
                                            >
                                                {(patientLoading || appointmentLoading) ? (
                                                    <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                ) : (
                                                    <>
                                                        <Wallet className="w-4 h-4" />
                                                        {t('medical_history.pay_button', 'Pay')}
                                                    </>
                                                )}
                                            </button>
                                        )}
                                    </div>
                                )}

                                {/* Meeting recording (video + transcript) — shown for ANY status,
                                    so the doctor sees "Generating…" / View as soon as a Visio
                                    meeting is recorded, not only after completion. */}
                                <MeetingSummary
                                    appointmentId={selectedAppointment.appointmentId}
                                    expectRecording={/visio/i.test(selectedAppointment?.appointmentMode || '')}
                                    className="mb-4 sm:mb-6"
                                />

                                {selectedTab === 'Rescheduling' && currentRecommendation ? (
                                    <>
                                        {/* Appointment Details */}
                                        {/* <div className="mb-4 p-4 bg-gradient-to-br from-[#4ABDBD]/5 to-[#3A9B9B]/5 border border-[#4ABDBD]/20 rounded-xl">
                                            <h4 className="text-lg font-semibold text-gray-800 mb-3">{t('patientappointment.appointment_details_heading')}</h4>
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                                                <div>
                                                    <span className="font-medium text-gray-600">{t('patientappointment.date_time_label')}:</span>
                                                    <p className="text-gray-800">{currentRecommendation.localTime ? format(new Date(currentRecommendation.localTime), 'MMM d, yyyy h:mm a') : '-'}</p>
                                                </div>
                                                <div>
                                                    <span className="font-medium text-gray-600">{t('patientappointment.speciality_label')}:</span>
                                                    <p className="text-gray-800">{currentRecommendation.specialtyName || t('medical_history.na')}</p>
                                                </div>
                                                <div>
                                                    <span className="font-medium text-gray-600">{t('patientappointment.appointment_type_label')}:</span>
                                                    <p className="text-gray-800">{currentRecommendation.specialtyTypeName || currentRecommendation.appointTypeName || t('medical_history.na')}</p>
                                                </div>
                                                <div>
                                                    <span className="font-medium text-gray-600">{t('medical_history.status_label')}:</span>
                                                    <p className="text-gray-800">{currentRecommendation.status || t('medical_history.na')}</p>
                                                </div>
                                            </div>
                                        </div> */}

                                        {/* Recommended Slots */}
                                        <div className="mb-4">
                                            <h4 className="text-lg font-semibold text-gray-800 mb-2">{t('patientappointment.select_recommended_slots_heading')}</h4>
                                            <p className="text-sm text-gray-600 mb-4">{t('patientappointment.reschedule_recommendation_message')}</p>
                                            <div className="flex flex-wrap items-center gap-3">
                                                {/* Get recommendation data - merge currentRecommendation and selectedAppointment */}
                                                {(() => {
                                                    const recommendation = { ...currentRecommendation, ...selectedAppointment };

                                                    // Helper function to create slot datetime from date and time or use direct date
                                                    const createSlotDateTime = (dateField, timeField) => {
                                                        if (!dateField) return null;
                                                        // If date_1 is already a full datetime string, use it directly
                                                        if (dateField.includes('T') || dateField.includes(' ')) {
                                                            return new Date(dateField);
                                                        }
                                                        // Otherwise combine date and time
                                                        if (timeField) {
                                                            return new Date(`${dateField}T${timeField}`);
                                                        }
                                                        return new Date(dateField);
                                                    };

                                                    const slot1 = createSlotDateTime(recommendation?.date_1, recommendation?.time_1);
                                                    const slot2 = createSlotDateTime(recommendation?.date_2, recommendation?.time_2);
                                                    const slot3 = createSlotDateTime(recommendation?.date_3, recommendation?.time_3);

                                                    const hasSlots = slot1 || slot2 || slot3;

                                                    if (!hasSlots) {
                                                        return (
                                                            <p className="text-sm text-gray-500 italic">
                                                                {t('medical_history.no_slots_available') || 'No recommended slots available'}
                                                            </p>
                                                        );
                                                    }

                                                    return (
                                                        <>
                                                            {slot1 && (
                                                                <button
                                                                    data-testid={`patient-medical-history-recommendation-slot1-${recommendation.id}`}
                                                                    onClick={() => handleRecommendationSlotClick(slot1.toISOString(), recommendation)}
                                                                    disabled={appointmentLoading}
                                                                    className="px-4 py-2 bg-gradient-to-br from-[#4ABDBD]/10 to-[#3AA8A8]/10 border-2 border-[#4ABDBD] rounded-xl hover:bg-gradient-to-br hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <div className="text-sm font-medium text-gray-800 hover:text-white">
                                                                        {format(slot1, 'MMM d, yyyy h:mm a')}
                                                                    </div>
                                                                </button>
                                                            )}
                                                            {slot2 && (
                                                                <button
                                                                    data-testid={`patient-medical-history-recommendation-slot2-${recommendation.id}`}
                                                                    onClick={() => handleRecommendationSlotClick(slot2.toISOString(), recommendation)}
                                                                    disabled={appointmentLoading}
                                                                    className="px-4 py-2 bg-gradient-to-br from-[#4ABDBD]/10 to-[#3AA8A8]/10 border-2 border-[#4ABDBD] rounded-xl hover:bg-gradient-to-br hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <div className="text-sm font-medium text-gray-800 hover:text-white">
                                                                        {format(slot2, 'MMM d, yyyy h:mm a')}
                                                                    </div>
                                                                </button>
                                                            )}
                                                            {slot3 && (
                                                                <button
                                                                    data-testid={`patient-medical-history-recommendation-slot3-${recommendation.id}`}
                                                                    onClick={() => handleRecommendationSlotClick(slot3.toISOString(), recommendation)}
                                                                    disabled={appointmentLoading}
                                                                    className="px-4 py-2 bg-gradient-to-br from-[#4ABDBD]/10 to-[#3AA8A8]/10 border-2 border-[#4ABDBD] rounded-xl hover:bg-gradient-to-br hover:from-[#4ABDBD] hover:to-[#3AA8A8] hover:text-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                                                                >
                                                                    <div className="text-sm font-medium text-gray-800 hover:text-white">
                                                                        {format(slot3, 'MMM d, yyyy h:mm a')}
                                                                    </div>
                                                                </button>
                                                            )}
                                                            <button
                                                                data-testid={`patient-medical-history-view-more-slots-${recommendation.id}`}
                                                                onClick={() => handleViewMoreSlotsInHistory(recommendation)}
                                                                disabled={appointmentLoading}
                                                                className="px-4 py-2 bg-white border-2 border-gray-400 text-gray-600 rounded-full text-sm font-medium hover:bg-gray-100 transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                {t('medical_history.view_more_slots_button') || 'View More Slots'}
                                                            </button>
                                                            <button
                                                                data-testid={`patient-medical-history-cancel-recommendation-${recommendation.id}`}
                                                                onClick={() => handleCancelRecommendationAppointment(recommendation)}
                                                                disabled={appointmentLoading}
                                                                className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 ml-auto"
                                                            >
                                                                {appointmentLoading && (
                                                                    <span className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                                                                )}
                                                                {t('patientappointment.cancel_appointment_button')}
                                                            </button>
                                                        </>
                                                    );
                                                })()}
                                            </div>
                                        </div>

                                        {/* View More Slots Section */}
                                        {isViewingMoreSlots && currentRecommendation && (
                                            <div className="mt-6 p-4 sm:p-6 bg-gradient-to-br from-yellow-50/50 to-orange-50/30 border border-yellow-200 rounded-xl">
                                                <h4 className="text-lg font-semibold text-gray-800 mb-2">
                                                    {t('medical_history.view_more_slots_title') || 'Select an Available Slot'}
                                                </h4>
                                                <p className="text-sm text-gray-600 mb-4">
                                                    {t('medical_history.view_more_slots_description') || 'Click on any available slot to reschedule your appointment'}
                                                </p>

                                                {appointmentLoading ? (
                                                    <div className="flex justify-center items-center py-8">
                                                        <span className="h-5 w-5 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin"></span>
                                                    </div>
                                                ) : (
                                                    <div className="space-y-6">
                                                        {/* Week Navigation - Always visible */}
                                                        {(() => {
                                                            const today = new Date();
                                                            const currentWeekMonday = getCurrentWeekMonday(today);
                                                            const startDate = addDays(currentWeekMonday, weekOffsetMore * 7);
                                                            const endDate = addDays(startDate, 6);
                                                            return (
                                                                <div className="flex items-center justify-center gap-4 sm:gap-6 mb-4">
                                                                    <button
                                                                        data-testid="patient-medical-history-prev-week-more-button"
                                                                        onClick={handlePrevWeekMore}
                                                                        disabled={weekOffsetMore === 0}
                                                                        className="p-2 bg-[#4ABDBD] text-white rounded-[16px] disabled:opacity-50 disabled:cursor-not-allowed shadow hover:bg-[#3AA8A8] transition-all duration-300 cursor-pointer"
                                                                    >
                                                                        <ArrowLeft size={20} />
                                                                    </button>
                                                                    <h3 className="text-lg font-semibold text-gray-800">
                                                                        {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
                                                                    </h3>
                                                                    <button
                                                                        data-testid="patient-medical-history-next-week-more-button"
                                                                        onClick={handleNextWeekMore}
                                                                        disabled={weekOffsetMore === 2}
                                                                        className="p-2 bg-[#4ABDBD] text-white rounded-[16px] disabled:opacity-50 disabled:cursor-not-allowed shadow hover:bg-[#3AA8A8] transition-all duration-300 cursor-pointer"
                                                                    >
                                                                        <ArrowRight size={20} />
                                                                    </button>
                                                                </div>
                                                            );
                                                        })()}

                                                        {/* Available Days - use availableSlots for patient, availableDoctorSlots for doctor */}
                                                        {(() => {
                                                            const slotsData = userRole === 'patient' ? availableSlots : availableDoctorSlots;
                                                            if (!slotsData || Object.keys(slotsData).length === 0) {
                                                                return (
                                                                    <div className="text-center py-8 text-gray-500">
                                                                        {t('medical_history.no_slots_available')}
                                                                    </div>
                                                                );
                                                            }
                                                            const availableDays = Object.entries(slotsData)
                                                                .map(([day, data]) => ({ day, ...data }))
                                                                .sort((a, b) => new Date(a.date) - new Date(b.date));

                                                            return (
                                                                <div className="grid grid-cols-1 gap-4">
                                                                    {availableDays.map(({ day, date, slots }) => {
                                                                        const hasAvailableSlots = slots.filter(slot => slot.status === 'available').length > 0;
                                                                        if (!hasAvailableSlots) return null;

                                                                        return (
                                                                            <div
                                                                                key={day}
                                                                                className="group overflow-hidden rounded-xl border border-gray-200 bg-gray-50 shadow-sm hover:shadow-[0_0px_8px_0_#4ABDBD] transition-all duration-300"
                                                                            >
                                                                                <div
                                                                                    className="p-4 bg-gray-50 flex items-center gap-3 cursor-pointer hover:bg-gray-100 transition-all duration-200"
                                                                                    onClick={() => toggleDayDrawerMore(day)}
                                                                                >
                                                                                    <CalendarCheck size={20} className="text-[#4ABDBD]" />
                                                                                    <h4 className="font-semibold text-gray-500 group-hover:text-[#4ABDBD] transition-colors truncate">
                                                                                        {format(new Date(date), 'EEEE')}, {format(new Date(date), 'MMM d, yyyy')}
                                                                                    </h4>
                                                                                </div>
                                                                                {openDayMore === day && (
                                                                                    <div className="p-4 bg-white border-t border-gray-200">
                                                                                        <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-2">
                                                                                            {slots
                                                                                                .filter(slot => slot.status === 'available')
                                                                                                .map((slot, idx) => (
                                                                                                    <button
                                                                                                        key={idx}
                                                                                                        type="button"
                                                                                                        data-testid={`patient-medical-history-direct-slot-${day}-${idx}`}
                                                                                                        onClick={() => handleDirectSlotClickInHistory(slot, currentRecommendation)}
                                                                                                        disabled={appointmentLoading}
                                                                                                        className="p-2 rounded-xl text-center text-sm transition-all duration-200 bg-gray-50 border border-gray-200 text-gray-700 hover:bg-[#4ABDBD] hover:text-white cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                                    >
                                                                                                        {format(new Date(slot.start), 'h:mm a')}
                                                                                                    </button>
                                                                                                ))}
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        );
                                                                    })}
                                                                </div>
                                                            );
                                                        })()}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : selectedTab === 'Completed' && !(userRole === 'doctor' && isEditingCompleted) ? (
                                    <>
                                        {userRole === 'doctor' && (
                                            <div className="flex justify-end mb-3 sm:mb-4">
                                                <button
                                                    type="button"
                                                    data-testid="patient-medical-history-edit-completed-button"
                                                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                                                    onClick={() => setIsEditingCompleted(true)}
                                                >
                                                    {t('medical_history.edit_button', 'Edit')}
                                                </button>
                                            </div>
                                        )}

                                        {/* Payment summary + (doctor/admin) mark paid / change method controls */}
                                        <CompletedPaymentCard
                                            appointment={selectedAppointment}
                                            canEdit={userRole === 'doctor' || userRole === 'admin'}
                                            onChanged={() => selectedCard.appointmentId && dispatch(getAppointmentById(selectedCard.appointmentId))}
                                        />

                                        {/* MeetingSummary now renders above for every tab. */}
                                        {/* AI appointment summary — shown once the appointment is completed */}
                                        <AppointmentSummary appointment={selectedAppointment} className="mb-4 sm:mb-6" />

                                        {visibleNoteFields.map((f) => ({
                                            title: f.name,
                                            value: notes[f.fieldId],
                                        })).map((section, index) => {

                                            // ❗ Skip if value is null / empty
                                            if (!section.value) return null;

                                            return (
                                                <div key={index} className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                                    <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">{section.title}</h2>
                                                    <p className="w-full p-3 border border-gray-300 rounded-xl bg-gray-50 text-sm sm:text-base text-gray-600 min-h-[100px] whitespace-pre-wrap">
                                                        {section.value}
                                                    </p>
                                                </div>
                                            );
                                        })}

                                        {/* Treatment Plan – Medicines (read-only view) — hidden on preprod branch */}
                                        {visibleStaticTypes.has('medicines') && (
                                            <PrescriptionSection
                                                appointment={selectedAppointment}
                                                readOnly
                                            />
                                        )}

                                        {/* Audio Section - Multiple Clips */}
                                        {visibleStaticTypes.has('audio') && (selectedAppointment?.audioUploads?.length > 0 || selectedAppointment?.voiceClipUrl) && (
                                            <div className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                                <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                                    {t('medical_history.upload_audio_heading')}
                                                    {selectedAppointment?.audioUploads?.length > 1 && (
                                                        <span className="text-xs text-gray-400 ml-2 font-normal">({selectedAppointment.audioUploads.length})</span>
                                                    )}
                                                </h2>
                                                {selectedAppointment?.audioUploads?.length > 0 ? (
                                                    <div className="flex flex-col gap-3">
                                                        {selectedAppointment.audioUploads.map((upload, idx) => (
                                                            <div key={upload.uploadId || idx} className="bg-white rounded-lg border border-gray-200 p-3 shadow-sm">
                                                                <span className="text-xs text-gray-500 font-medium">{t('medical_history.audio_clip_label', 'Audio')} {idx + 1}</span>
                                                                <audio controls src={getFileURL(upload.audioUrl)} className="w-full mt-2" />
                                                                {mediaTextBlock(upload.transcribedNotes, upload.status, t('medical_history.generating_transcript', 'Generating transcript…'))}
                                                            </div>
                                                        ))}
                                                    </div>
                                                ) : selectedAppointment?.voiceClipUrl && (
                                                    <>
                                                        <audio controls src={getFileURL(selectedAppointment.voiceClipUrl)} className="w-full max-w-md" />
                                                        {selectedAppointment.transcribedNotes && (
                                                            <p className="mt-2 w-full p-3 border border-gray-300 rounded bg-gray-50 text-sm sm:text-base text-gray-600 whitespace-pre-wrap">
                                                                {selectedAppointment.transcribedNotes}
                                                            </p>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                        {/* Image Section - Multiple Images */}
                                        {visibleStaticTypes.has('image') && (selectedAppointment?.imageUploads?.length > 0 || selectedAppointment?.imageUrl) && (
                                            <div className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                                <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                                    {t('medical_history.uploaded_image_label')}
                                                    {selectedAppointment?.imageUploads?.length > 1 && (
                                                        <span className="text-xs text-gray-400 ml-2 font-normal">({selectedAppointment.imageUploads.length})</span>
                                                    )}
                                                </h2>
                                                {selectedAppointment?.imageUploads?.length > 0 ? (
                                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                                                        {selectedAppointment.imageUploads.map((upload, idx) => (
                                                            <img
                                                                key={upload.uploadId || idx}
                                                                src={getFileURL(upload.imageUrl)}
                                                                alt={`${t('medical_history.uploaded_image_alt')} ${idx + 1}`}
                                                                className="w-full h-auto rounded-lg border border-gray-200 shadow-sm cursor-pointer hover:shadow-md transition-shadow"
                                                                onClick={() => window.open(getFileURL(upload.imageUrl), '_blank')}
                                                            />
                                                        ))}
                                                    </div>
                                                ) : selectedAppointment?.imageUrl && (
                                                    <img
                                                        src={getFileURL(selectedAppointment.imageUrl)}
                                                        alt={t('medical_history.uploaded_image_alt')}
                                                        className="w-full max-w-md h-auto rounded border border-gray-300"
                                                    />
                                                )}
                                            </div>
                                        )}
                                        {/* OCR Notes Section - All Notes (including still-processing ones, so the spinner shows) */}
                                        {visibleStaticTypes.has('image') && (selectedAppointment?.imageUploads?.some(u => u.ocrNotes || u.status === 'processing') || selectedAppointment?.ocrNotes) && (
                                            <div className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                                <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                                    {t('medical_history.ocr_notes_label')}
                                                </h2>
                                                {selectedAppointment?.imageUploads?.length > 0 ? (
                                                    selectedAppointment.imageUploads
                                                        .filter(u => u.ocrNotes || u.status === 'processing')
                                                        .map((upload, idx) => (
                                                            <React.Fragment key={upload.uploadId || idx}>
                                                                {mediaTextBlock(upload.ocrNotes, upload.status, t('medical_history.generating_ocr', 'Generating OCR notes…'))}
                                                            </React.Fragment>
                                                        ))
                                                ) : selectedAppointment?.ocrNotes && (
                                                    <p className="w-full p-3 border border-gray-300 rounded bg-gray-50 text-sm sm:text-base text-gray-600 min-h-[100px]">
                                                        {selectedAppointment.ocrNotes}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </>
                                ) : selectedTab !== 'Cancelled' && selectedTab !== 'NoShow' && (
                                    (userRole === 'doctor' && selectedTab === 'Booked' && isWithinTwoHoursBeforeAppointment()) ||
                                    (userRole === 'doctor' && selectedTab === 'Completed' && isEditingCompleted)
                                ) ? (
                                    <>
                                        {selectedTab === 'Completed' && (
                                            <div className="flex justify-end mb-3 sm:mb-4">
                                                <button
                                                    type="button"
                                                    data-testid="patient-medical-history-cancel-edit-completed-button"
                                                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                                                    onClick={() => {
                                                        if (hasUnsavedChanges()) {
                                                            confirmUnsavedChanges(() => setIsEditingCompleted(false));
                                                        } else {
                                                            setIsEditingCompleted(false);
                                                        }
                                                    }}
                                                    disabled={isSaving}
                                                >
                                                    {t('medical_history.cancel_button', 'Cancel')}
                                                </button>
                                            </div>
                                        )}
                                        {visibleNoteFields.map((f) => ({
                                            title: f.name,
                                            section: f.fieldId,
                                            value: notes[f.fieldId] ?? '',
                                        })).map((section, index) => (
                                            <div key={index} className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                                <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">{section.title}</h2>
                                                <textarea
                                                    data-testid={`patient-medical-history-notes-textarea-${section.section}`}
                                                    placeholder={t('medical_history.textarea_placeholder')}
                                                    value={section.value}
                                                    onChange={(e) => {
                                                        if (isInputDisabled()) return;

                                                        const value = e.target.value;

                                                        // Sanitize input
                                                        const sanitized = value.replace(/[<>]/g, '');

                                                        // Enforce max length
                                                        const finalValue = sanitized.length > MAX_NOTES_LENGTH
                                                            ? sanitized.substring(0, MAX_NOTES_LENGTH)
                                                            : sanitized;

                                                        // Validate text pattern
                                                        if (isValidTextInput(finalValue)) {
                                                            setNotes((prev) => ({
                                                                ...prev,
                                                                [section.section]: finalValue
                                                            }));

                                                            // Clear error if valid
                                                            const error = validateNotesInput(finalValue, t);
                                                            setNotesErrors((prev) => ({
                                                                ...prev,
                                                                [section.section]: error
                                                            }));
                                                        }
                                                    }}
                                                    disabled={isInputDisabled()}
                                                    maxLength={MAX_NOTES_LENGTH}
                                                    className={`w-full p-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-[#4ABDBD] hover:border-[#4ABDBD] resize-none ${expandedSections[section.section] ? 'min-h-[300px] sm:min-h-[400px]' : 'min-h-[100px]'} text-sm transition-all duration-300 ${notesErrors[section.section]
                                                        ? 'border-red-300 bg-red-50'
                                                        : 'border-gray-300'
                                                        }`}
                                                />
                                                {notesErrors[section.section] && (
                                                    <p className="text-red-600 text-xs mt-1">{notesErrors[section.section]}</p>
                                                )}
                                                {(userRole === 'doctor' && (selectedTab === 'Booked' || (selectedTab === 'Completed' && isEditingCompleted))) && (
                                                    <div className="flex justify-end gap-2 sm:gap-3">
                                                        <button
                                                            type="button"
                                                            data-testid={`patient-medical-history-notes-expand-button-${section.section}`}
                                                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 shadow-md hover:shadow-lg hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                                                            onClick={() => setExpandedSections((prev) => ({ ...prev, [section.section]: !prev[section.section] }))}
                                                            disabled={isInputDisabled()}
                                                        >
                                                            {expandedSections[section.section]
                                                                ? t('medical_history.collapse_button')
                                                                : t('medical_history.expand_button')}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            data-testid={`patient-medical-history-notes-save-button-${section.section}`}
                                                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2 disabled:opacity-50"
                                                            onClick={() => handleSaveNotes(section.section)}
                                                            disabled={isSaveButtonDisabled(`notes:${section.section}`)}
                                                        >
                                                            {savingField === `notes:${section.section}` && (
                                                                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                            )}
                                                            {t('medical_history.save_button')}
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                        {/* Treatment Plan – Medicines — hidden on preprod branch */}
                                        {visibleStaticTypes.has('medicines') && (
                                            <PrescriptionSection
                                                appointment={selectedAppointment}
                                                readOnly={isInputDisabled()}
                                            />
                                        )}
                                        {/* Audio Upload Section - Multiple Clips */}
                                        {visibleStaticTypes.has('audio') && (
                                        <div className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                            <div className="flex items-center justify-between">
                                                <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                                    {t('medical_history.upload_audio_heading')}
                                                </h2>
                                                <span className="text-xs text-gray-500">
                                                    {(selectedAppointment?.audioUploads || []).length}/5
                                                </span>
                                            </div>
                                            {(selectedAppointment?.audioUploads || []).length < 5 && (
                                            <div className="relative">
                                                <input
                                                    type="file"
                                                    accept="audio/*"
                                                    onChange={handleAudioChange}
                                                    disabled={isInputDisabled()}
                                                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                    data-testid="audio-upload-input"
                                                />
                                                <div className="border-2 border-dashed border-[#4ABDBD] rounded-xl p-8 text-center bg-gradient-to-br from-[#4ABDBD]/5 to-[#3AA8A8]/5 hover:from-[#4ABDBD]/10 hover:to-[#3AA8A8]/10 transition-all duration-300 hover:border-[#3AA8A8]">
                                                    <div className="flex flex-col items-center gap-4">
                                                        <div className="w-16 h-16 bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] rounded-full flex items-center justify-center">
                                                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <p className="text-base font-semibold text-[#4ABDBD] mb-1">
                                                                {t('medical_history.click_to_upload_audio')}
                                                            </p>
                                                            <p className="text-xs text-gray-500">
                                                                {t('medical_history.audio_format_hint')}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                            )}
                                            {audioError && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-2">{audioError}</p>
                                            )}
                                            {audioPreview && (
                                                <div className="mt-3 sm:mt-4 bg-white rounded-xl border border-gray-200 p-3">
                                                    <p className="text-xs sm:text-sm font-semibold mb-2">
                                                        {t('medical_history.audio_preview_label')}
                                                    </p>
                                                    <audio controls src={audioPreview} className="w-full max-w-md" />
                                                    <div className="flex justify-end gap-2 sm:gap-3 mt-3">
                                                        <button
                                                            type="button"
                                                            data-testid="patient-medical-history-audio-clear-button"
                                                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                                                            onClick={() => {
                                                                setAudioFile(null);
                                                                setAudioPreview(null);
                                                                setAudioError('');
                                                            }}
                                                            disabled={isInputDisabled()}
                                                        >
                                                            {t('medical_history.clear_button')}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            data-testid="patient-medical-history-audio-save-button"
                                                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2 disabled:opacity-50"
                                                            onClick={handleSaveAudio}
                                                            disabled={isSaveButtonDisabled('audio')}
                                                        >
                                                            {savingField === 'audio' && (
                                                                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                            )}
                                                            {t('medical_history.save_button')}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                            {/* Uploaded audio clips — player + transcript + delete */}
                                            {(selectedAppointment?.audioUploads || []).length > 0 && (
                                                <div className="mt-3 flex flex-col gap-3">
                                                    {selectedAppointment.audioUploads.map((upload, idx) => (
                                                        <div key={upload.uploadId || idx} className="relative bg-white rounded-xl border border-gray-200 p-3 shadow-sm">
                                                            <div className="flex items-center justify-between mb-2">
                                                                <span className="text-xs text-gray-500 font-medium">{t('medical_history.audio_clip_label', 'Audio')} {idx + 1}</span>
                                                                {!isInputDisabled() && (
                                                                    <button
                                                                        type="button"
                                                                        data-testid={`patient-medical-history-delete-audio-${upload.uploadId}`}
                                                                        onClick={() => handleDeleteAudioUpload(upload.uploadId)}
                                                                        className="text-red-500 hover:text-red-600 p-1 rounded-lg transition-all duration-200 cursor-pointer"
                                                                        title={t('medical_history.remove_audio', 'Remove')}
                                                                    >
                                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <audio controls src={getFileURL(upload.audioUrl)} className="w-full" />
                                                            {mediaTextBlock(upload.transcribedNotes, upload.status, t('medical_history.generating_transcript', 'Generating transcript…'))}
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            {/* Backward compatibility: old single voiceClipUrl */}
                                            {(!selectedAppointment?.audioUploads || selectedAppointment.audioUploads.length === 0) && selectedAppointment?.voiceClipUrl && (
                                                <div className="mt-3 bg-white rounded-xl border border-gray-200 p-3">
                                                    <audio controls src={getFileURL(selectedAppointment.voiceClipUrl)} className="w-full max-w-md" />
                                                    {selectedAppointment.transcribedNotes && (
                                                        <p className="mt-2 w-full p-3 border border-gray-300 rounded-xl bg-gray-50 text-sm sm:text-base text-gray-600 whitespace-pre-wrap">
                                                            {selectedAppointment.transcribedNotes}
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        )}
                                        {/* Image Upload Section */}
                                        {visibleStaticTypes.has('image') && (
                                        <div className="flex flex-col w-full gap-2 mb-4 sm:mb-6">
                                            <div className="flex items-center justify-between">
                                                <h2 className="text-base sm:text-lg font-semibold text-[#4ABDBD]">
                                                    {t('medical_history.upload_image_heading')}
                                                </h2>
                                                <span className="text-xs text-gray-500">
                                                    {(selectedAppointment?.imageUploads || []).length}/5
                                                </span>
                                            </div>

                                            {/* Upload box - hidden when 5 images reached */}
                                            {(selectedAppointment?.imageUploads || []).length < 5 && (
                                                <div className="relative">
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        onChange={handleImageChange}
                                                        disabled={isInputDisabled()}
                                                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
                                                        data-testid="image-upload-input"
                                                    />
                                                    <div className="border-2 border-dashed border-[#4ABDBD] rounded-xl p-6 text-center bg-gradient-to-br from-[#4ABDBD]/5 to-[#3AA8A8]/5 hover:from-[#4ABDBD]/10 hover:to-[#3AA8A8]/10 transition-all duration-300 hover:border-[#3AA8A8]">
                                                        <div className="flex flex-col items-center gap-3">
                                                            <div className="w-12 h-12 bg-gradient-to-br from-[#4ABDBD] to-[#3AA8A8] rounded-full flex items-center justify-center">
                                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                </svg>
                                                            </div>
                                                            <div>
                                                                <p className="text-sm font-semibold text-[#4ABDBD] mb-1">
                                                                    {t('medical_history.click_to_upload_image')}
                                                                </p>
                                                                <p className="text-xs text-gray-500">
                                                                    {t('medical_history.image_format_hint')}
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {imageError && (
                                                <p className="text-red-600 text-xs sm:text-sm mt-2">{imageError}</p>
                                            )}

                                            {/* New image preview + save/clear */}
                                            {imagePreview && (
                                                <div className="mt-3 bg-white rounded-xl border border-gray-200 p-3">
                                                    <img
                                                        src={imagePreview}
                                                        alt={t('medical_history.image_alt')}
                                                        className="w-full max-w-md h-auto rounded-lg border border-gray-200"
                                                    />
                                                    <div className="flex justify-end gap-2 mt-3">
                                                        <button
                                                            type="button"
                                                            data-testid="patient-medical-history-image-clear-button"
                                                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                                                            onClick={() => { setImageFile(null); setImagePreview(null); setImageError(''); }}
                                                            disabled={isInputDisabled()}
                                                        >
                                                            {t('medical_history.clear_button')}
                                                        </button>
                                                        <button
                                                            type="button"
                                                            data-testid="patient-medical-history-image-save-button"
                                                            className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2 disabled:opacity-50"
                                                            onClick={handleSaveImage}
                                                            disabled={isSaveButtonDisabled('image')}
                                                        >
                                                            {savingField === 'image' && (
                                                                <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                            )}
                                                            {t('medical_history.save_button')}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Uploaded images - small thumbnails with eye + delete */}
                                            {(selectedAppointment?.imageUploads || []).length > 0 && (
                                                <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3">
                                                    {selectedAppointment.imageUploads.map((upload, idx) => (
                                                        <div key={upload.uploadId} className="relative group bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200">
                                                            <img
                                                                src={getFileURL(upload.imageUrl)}
                                                                alt={`${t('medical_history.uploaded_image_alt')} ${idx + 1}`}
                                                                className="w-full h-20 object-cover"
                                                            />
                                                            {upload.status === 'processing' && (
                                                                <div className="absolute top-1 left-1 bg-white/90 rounded-full p-1" title={t('medical_history.generating_ocr', 'Generating OCR notes…')}>
                                                                    <span className="h-3 w-3 border-2 border-gray-300 border-t-[#4ABDBD] rounded-full animate-spin inline-block" />
                                                                </div>
                                                            )}
                                                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200 flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                                                                <button
                                                                    type="button"
                                                                    data-testid={`patient-medical-history-view-image-${upload.uploadId}`}
                                                                    onClick={() => {
                                                                        if (detailsContainerRef.current) {
                                                                            scrollPositionRef.current = detailsContainerRef.current.scrollTop;
                                                                        }
                                                                        setViewingImageUpload(upload.uploadId);
                                                                    }}
                                                                    className="bg-white/90 hover:bg-white text-[#4ABDBD] p-1.5 rounded-lg transition-all duration-200 cursor-pointer"
                                                                    title={t('medical_history.view_image', 'View')}
                                                                >
                                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                    </svg>
                                                                </button>
                                                                {!isInputDisabled() && (
                                                                    <button
                                                                        type="button"
                                                                        data-testid={`patient-medical-history-delete-image-${upload.uploadId}`}
                                                                        onClick={() => handleDeleteImage(upload.uploadId)}
                                                                        className="bg-white/90 hover:bg-white text-red-500 p-1.5 rounded-lg transition-all duration-200 cursor-pointer"
                                                                        title={t('medical_history.remove_image', 'Remove')}
                                                                    >
                                                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                            </div>
                                                            <div className="px-2 py-1 text-center">
                                                                <span className="text-[10px] text-gray-500 font-medium">{idx + 1}</span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Backward compatibility: old single imageUrl as thumbnail */}
                                            {(!selectedAppointment?.imageUploads || selectedAppointment.imageUploads.length === 0) && selectedAppointment?.imageUrl && (
                                                <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3">
                                                    <div className="relative group bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-200">
                                                        <img
                                                            src={getFileURL(selectedAppointment.imageUrl)}
                                                            alt={t('medical_history.uploaded_image_alt')}
                                                            className="w-full h-20 object-cover"
                                                        />
                                                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-200 flex items-center justify-center opacity-0 group-hover:opacity-100">
                                                            <button
                                                                type="button"
                                                                data-testid="patient-medical-history-view-legacy-image-button"
                                                                onClick={() => {
                                                                    if (detailsContainerRef.current) {
                                                                        scrollPositionRef.current = detailsContainerRef.current.scrollTop;
                                                                    }
                                                                    setViewingImageUpload('legacy');
                                                                }}
                                                                className="bg-white/90 hover:bg-white text-[#4ABDBD] p-1.5 rounded-lg transition-all duration-200 cursor-pointer"
                                                            >
                                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                                                </svg>
                                                            </button>
                                                        </div>
                                                        <div className="px-2 py-1 text-center">
                                                            <span className="text-[10px] text-gray-500 font-medium">1</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                        )}
                                        {(userRole === 'doctor' && selectedTab === 'Booked' && selectedAppointment.status === 'Booked') && (
                                            <div className="flex justify-end items-center gap-3 mb-4">
                                                {isPaidStatus(selectedAppointment.payment?.status) && (
                                                    <span data-testid="patient-payment-paid-badge" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-green-50 text-green-600 font-semibold text-sm">
                                                        <CheckCircle2 className="w-4 h-4" />
                                                        {t('payments.paid', 'Paid')}
                                                    </span>
                                                )}
                                                <button
                                                    type="button"
                                                    data-testid="patient-medical-history-complete-appointment-button"
                                                    className="px-4 py-2 rounded-xl bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center justify-center"
                                                    onClick={handleCompleteClick}
                                                    disabled={isInputDisabled() || patientLoading || appointmentLoading}
                                                >
                                                    {(patientLoading || appointmentLoading) ? (
                                                        <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                                                    ) : (
                                                        t('medical_history.complete_button')
                                                    )}
                                                </button>
                                            </div>
                                        )}

                                    </>
                                ) : (
                                    <div className='flex items-center justify-center'>
                                        <h2 className="text-xl sm:text-4xl font-semibold text-red-500">
                                            {t(`medical_history.tabs.${selectedTab.toLowerCase()}`)}
                                        </h2>
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="flex flex-col items-center justify-center min-h-[50vh] text-center">
                                <img
                                    src={medicalhistory}
                                    alt={t('medical_history.select_appointment_alt')}
                                    className="w-2/3 sm:w-1/2 md:w-1/3 lg:w-1/4 max-w-[200px] mb-4"
                                />
                                <p className="text-base sm:text-lg font-semibold text-gray-600">
                                    {t('medical_history.select_appointment_message')}
                                </p>
                                <p className="text-xs sm:text-sm text-gray-500 mt-2">
                                    {t('medical_history.select_appointment_submessage')}
                                </p>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Payment Modal */}
            {showPaymentModal && (
                <div data-testid="payment-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
                    <div data-testid="payment-modal-content" className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-md flex flex-col">
                        {/* Header Section - Sticky */}
                        <div data-testid="payment-modal-header" className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                            <h2 data-testid="payment-modal-title" className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">
                                {t('medical_history.invoice_heading', 'Invoice')}
                            </h2>
                            <button
                                data-testid="close-payment-modal"
                                id="btn-close-modal"
                                onClick={() => setShowPaymentModal(false)}
                                className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                            >
                                <X data-testid="close-modal-icon-payment" className="h-5 w-5 sm:h-6 sm:w-6" />
                            </button>
                        </div>

                        {(() => {
                            const isPayMode = paymentModalMode === 'pay';
                            const alreadyPaid = isPaidStatus(selectedAppointment?.payment?.status);
                            // Offline methods (no paylater). In 'pay' mode the patient can't
                            // choose cash — it's collected in person — so it's filtered out.
                            const baseMethods = (completePaymentOptions?.methods?.length
                                ? completePaymentOptions.methods
                                : FALLBACK_COMPLETE_METHODS).filter((m) => !m.requiresGateway && m.key !== 'paylater');
                            const payable = isPayMode ? baseMethods.filter((m) => m.key !== 'cash') : baseMethods;
                            const completeMethods = payable.length ? payable : (isPayMode ? PATIENT_PAY_METHODS : FALLBACK_COMPLETE_METHODS);
                            const selectedLabel = completeMethods.find((m) => m.key === paymentMethod)?.label || paymentMethod;
                            const saving = patientLoading || appointmentLoading || savingField === 'payment';
                            // In 'complete' mode the doctor doesn't re-pick a method if the
                            // patient already settled it online — just confirm completion.
                            const showMethodPicker = completeMethods.length > 1 && !(alreadyPaid && !isPayMode);
                            const submitLabel = isPayMode
                                ? `${t('medical_history.pay_with', 'Pay with')} ${selectedLabel}`
                                : (alreadyPaid
                                    ? t('medical_history.complete_appointment', 'Complete appointment')
                                    : `${t('medical_history.continue_as', 'Continue as')} ${selectedLabel}`);
                            return (
                                <>
                                    {/* Invoice body */}
                                    <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar space-y-4">
                                        {/* Top: payment method + status */}
                                        <div className="flex items-center justify-between gap-3 rounded-xl border border-gray-200 p-3">
                                            <div className="flex items-center gap-2">
                                                <Wallet className="w-4 h-4 text-[#4ABDBD]" />
                                                <div>
                                                    <p className="text-[11px] text-gray-400">{t('medical_history.payment_method_heading')}</p>
                                                    <p data-testid="invoice-method" className="text-sm font-semibold text-gray-800">{selectedLabel}</p>
                                                </div>
                                            </div>
                                            <span data-testid="invoice-status" className={`inline-flex items-center text-[11px] font-semibold px-2 py-0.5 rounded-full ${alreadyPaid ? 'text-green-600 bg-green-50' : 'text-amber-600 bg-amber-50'}`}>
                                                {alreadyPaid ? t('payments.paid', 'Paid') : t('payments.not_paid', 'Not paid')}
                                            </span>
                                        </div>

                                        {/* Appointment details */}
                                        <div className="rounded-xl border border-gray-200 p-3 space-y-2">
                                            <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400">{t('medical_history.appointment_details', 'Appointment details')}</p>
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-gray-500">{t('medical_history.price_label')}</span>
                                                <span className="font-semibold text-gray-700">{selectedAppointment.price ? `${getCurrencySymbol(selectedAppointment.currency)}${selectedAppointment.price}` : t('medical_history.na')}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-gray-500">{t('medical_history.date_label')}</span>
                                                <span className="font-semibold text-gray-700">{selectedCard.date} {selectedCard.time}</span>
                                            </div>
                                            <div className="flex items-center justify-between text-sm">
                                                <span className="text-gray-500">{t('medical_history.mode_label')}</span>
                                                <span className="font-semibold text-gray-700">{selectedAppointment.appointmentMode === 'onsite' ? t('appointmentdetails.Onsite') : t('medicalHistory.visio')}</span>
                                            </div>
                                        </div>

                                        {/* Method options — when more than one is offered (hidden once
                                            the patient has already paid and the doctor is just completing). */}
                                        {showMethodPicker && (
                                            <div data-testid="invoice-method-options">
                                                <p className="text-[11px] font-bold uppercase tracking-wide text-gray-400 mb-2">{t('payments.choose_method', 'Choose a method')}</p>
                                                <div className="grid grid-cols-3 gap-2">
                                                    {completeMethods.map((m) => (
                                                        <button
                                                            key={m.key}
                                                            type="button"
                                                            onClick={() => setPaymentMethod(m.key)}
                                                            data-testid={`invoice-method-${m.key}`}
                                                            disabled={saving}
                                                            className={`text-sm px-3 py-2 rounded-xl border transition ${paymentMethod === m.key ? 'border-[#4ABDBD] bg-[#4ABDBD]/10 text-[#3AA8A8] font-semibold' : 'border-gray-200 text-gray-600 hover:border-[#4ABDBD]'}`}
                                                        >
                                                            {m.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    {/* Footer Section - Sticky */}
                                    <div data-testid="form-actions-container-payment" className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                                        <button
                                            data-testid="cancel-button"
                                            id="btn-cancel"
                                            type="button"
                                            onClick={() => setShowPaymentModal(false)}
                                            className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                                            disabled={saving}
                                        >
                                            {t('medical_history.cancel_button')}
                                        </button>
                                        <button
                                            data-testid="pay-button"
                                            id="btn-submit"
                                            type="button"
                                            onClick={handlePaymentSubmit}
                                            className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm flex items-center gap-2 cursor-pointer"
                                            disabled={saving}
                                        >
                                            {saving && (<span data-testid="loading-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>)}
                                            {saving ? t('medical_history.loading_appointment') : submitLabel}
                                        </button>
                                    </div>
                                </>
                            );
                        })()}
                    </div>
                </div>
            )}

            <SummaryDrawer
                isOpen={isSummaryDrawerOpen}
                onClose={() => setIsSummaryDrawerOpen(false)}
                patientId={selectedPatient?.patientId}
                appointments={medicalHistoryData || []}
            />
            <AskAIDrawer isOpen={isAskAIDrawerOpen} onClose={() => setIsAskAIDrawerOpen(false)} />

        </div >
    );
};

export default MedicalHistory;